import { useState } from "react";

import {
  ANCHORS,
  ANCHOR_IDS,
  ANCHOR_TECHNIQUES,
  HOLDING_GROUNDS,
  HOLDING_GROUND_IDS,
  MOORING_TECHNIQUES,
  SCOPE_GUIDELINES,
} from "@nav-games/anchoring";
import {
  CHARTWORK_IDS,
  CHARTWORK_SYMBOLS,
  CHARTWORK_TECHNIQUES,
  CHARTWORK_TECHNIQUE_IDS,
} from "@nav-games/chartwork";
import { NATO_DIGITS, NATO_LETTERS } from "@nav-games/comms";
import { DATUMS, TRACK_TERMS } from "@nav-games/gps";
import {
  PORT_SIGNALS,
  TECHNIQUES,
  TECHNIQUE_IDS,
} from "@nav-games/pilotage";
import { TIDAL_LEVELS, TIDAL_MEASUREMENTS } from "@nav-games/tides";
import {
  IALA_IDS,
  IALA_MARKS,
  REGIONS,
  lightForRegion,
  regionLabel,
  type Region,
} from "@nav-games/iala";
import { IRPCS_RULES } from "@nav-games/irpcs";
import {
  DISTRESS_CATEGORIES,
  DISTRESS_SIGNALS,
  categoryLabel as distressCategoryLabel,
  type DistressSignal,
} from "@nav-games/distress";
import { ICS_COMBOS, ICS_FLAGS } from "@nav-games/flags";
import {
  KNOTS,
  KNOT_CATEGORIES,
  categoryLabel as knotCategoryLabel,
  type Knot,
} from "@nav-games/knots";
import {
  SAMPLE_DEVIATION_CARD,
  formatError as formatCompassError,
  formatHeading,
} from "@nav-games/compass";
import {
  NAV_PLANETS,
  NAV_STARS,
  formatDeclination,
  formatDegMin,
  moonPosition,
  planetPosition,
  sunPosition,
} from "@nav-games/astro";
import { ITU_CODE } from "@nav-games/morse";
import { NAV_LIGHTS, VESSEL_IDS, type Viewpoint } from "@nav-games/nav-lights";
import { SEMAPHORE_CODE, SEMAPHORE_NUMERALS } from "@nav-games/semaphore";
import {
  BEAUFORT_SCALE,
  WEATHER_CATEGORIES,
  WEATHER_TERMS,
  categoryLabel,
  type WeatherTerm,
} from "@nav-games/weather";

import { BuoyFigure } from "../ui/BuoyFigure";
import { ChartworkSymbol } from "../ui/ChartworkSymbol";
import { ChartworkTechniqueFigure } from "../ui/ChartworkTechniqueFigure";
import { CompassCadetDiagram } from "../ui/CompassCadetDiagram";
import { CompassCalculator } from "../ui/CompassCalculator";
import { CtsCalculator } from "../ui/CtsCalculator";
import { MeridianAltitudeCalculator } from "../ui/MeridianAltitudeCalculator";
import { SextantCorrectionCalculator } from "../ui/SextantCorrectionCalculator";
import { SextantFigure } from "../ui/SextantFigure";
import {
  type SextantErrorKind,
  SextantErrorFigure,
  sextantErrorExplain,
  sextantErrorLabel,
} from "../ui/SextantErrorFigure";
import { SightReductionCalculator } from "../ui/SightReductionCalculator";
import { TwilightCalculator } from "../ui/TwilightCalculator";
import { AlmanacIncrementCalculator } from "../ui/AlmanacIncrementCalculator";
import { MoonPhasePanel } from "../ui/MoonPhasePanel";
import { PlanetVisibilityPanel } from "../ui/PlanetVisibilityPanel";
import { StarChart } from "../ui/StarChart";
import { SailPartsFigure } from "../ui/SailPartsFigure";
import { PointsOfSailFigure } from "../ui/PointsOfSailFigure";
import { BoatPartsFigure } from "../ui/BoatPartsFigure";
import {
  BOAT_PARTS,
  POINTS_OF_SAIL,
  SAIL_MANOEUVRES,
  SAIL_PARTS,
  WOBBLE_STEPS,
  type BoatPart,
  type BoatView,
  type ManoeuvreStep,
  partsForView,
} from "@nav-games/sailing";
import { MANOEUVRES, SEARCH_PATTERNS } from "@nav-games/mob";
import {
  CONSUMABLES,
  CREW_BRIEF,
} from "@nav-games/predeparture";
import { SearchPatternFigure } from "../ui/SearchPatternFigure";
import { ProvisioningCalculator } from "../ui/ProvisioningCalculator";
import {
  DIRECTION_OF_BUOYAGE_NOTE,
  IWW_MARKS,
  REGION_DIFFERENCES,
  REGION_GEOGRAPHY,
} from "@nav-games/iala";
import {
  CONTROLLED_STOP,
  HULL_MODES,
  SEA_HANDLING,
  STOPPING_DISTANCES,
  TRIM_SETTINGS,
  TURNING_MODES,
} from "@nav-games/powerboat";
import {
  COMMON_TIDAL_GATES,
  COMMON_TRIGGERS,
  WORKFLOW,
} from "@nav-games/passage";
import {
  SAIL_REPAIRS,
  SPLICES,
  WHIPPINGS,
} from "@nav-games/knots";
import {
  ADVANCED_TECHNIQUES,
  LAUNCH_PROCEDURES,
} from "@nav-games/sailing";
import { ADVANCED_FIXES } from "@nav-games/pilotage";
import {
  TIDAL_STREAM_SOURCES,
  XTE_NOTES,
} from "@nav-games/tides";
import {
  ISOBAR_WIND_RULES,
  SHIPPING_FORECAST_STRUCTURE,
  TRANSCRIPTION_TIPS,
} from "@nav-games/weather";
import {
  RADIO_LICENCES,
  RT_LOG_COLUMNS,
  RT_LOG_RETENTION,
} from "@nav-games/comms";
import {
  BOARDING_RULES,
  DAVITS,
  PAINTERS,
  TENDERS,
  TOWING_TECHNIQUES,
} from "@nav-games/tender";
import {
  ENGINE_SYSTEMS,
  KILL_CORD_RULES,
  OUTBOARDS,
  OUTBOARD_PROCEDURES,
  TROUBLESHOOTING_TREE,
} from "@nav-games/engine";
import {
  CHART_SYMBOLS,
  DATUMS as CHART_DATUMS,
  PROJECTIONS,
} from "@nav-games/chartwork";
import { findMnemonic, MNEMONICS } from "@nav-games/mnemonics";
import { MnemonicCard } from "../ui/MnemonicCard";
import { RuleOfTwelfthsCalculator } from "../ui/RuleOfTwelfthsCalculator";
import { SecondaryPortCalculator } from "../ui/SecondaryPortCalculator";
import { ThreePointFixCalculator } from "../ui/ThreePointFixCalculator";
import { FrontCrossSectionFigure } from "../ui/FrontCrossSectionFigure";
import { BuysBallotFigure } from "../ui/BuysBallotFigure";
import { SynopticChartFigure } from "../ui/SynopticChartFigure";
import {
  CLOUDS_BY_LEVEL,
  COASTAL_BREEZES,
  DEPRESSION_LIFECYCLE,
  FOG_TYPES,
  FRONTS,
  PRESSURE_FEATURES,
} from "@nav-games/weather";
import {
  VHF_CALL_TYPES,
  VHF_CHANNELS,
  type VhfChannel,
} from "@nav-games/comms";
import { MmsiDecoder } from "../ui/MmsiDecoder";
import {
  BEACONS,
  CAPSIZE_SCENARIOS,
  CAT_C_DIFFERENCES,
  FIRST_AID_KITS,
  FIRST_AID_SCENARIOS,
  FLARE_PACKS,
  FLARE_SAFETY,
  FLARES,
  GRAB_BAG,
  LIFEJACKETS,
  LIFERAFTS,
  LIFERAFT_LAUNCH_STEPS,
  REEFING_BANDS,
  SAMPLE_AVS_DEG,
  STABILITY_CONCEPTS,
  STORM_SAILS,
  STORM_TACTICS,
  TRS_RULES,
  TRS_ZONES,
} from "@nav-games/safety";
import { GzCurveFigure } from "../ui/GzCurveFigure";
import { TrsZonesFigure } from "../ui/TrsZonesFigure";
import {
  ENGINE_FAULTS,
  PROCEDURES as ENGINE_PROCEDURES,
} from "@nav-games/engine";
import { LightRhythm } from "../ui/LightRhythm";
import { MorseTree } from "../ui/MorseTree";
import { SignalStack } from "../ui/SignalStack";
import { DayShapeFigure } from "../ui/DayShapeFigure";
import { Flag } from "../ui/Flag";
import { FlagHoist } from "../ui/FlagHoist";
import { AnchorFigure, ANCHOR_PHOTO_CREDITS } from "../ui/AnchorFigure";
import { ChartDatumsFigure } from "../ui/ChartDatumsFigure";
import { DaytimeVesselFigure } from "../ui/DaytimeVesselFigure";
import { FogPlayButton } from "../ui/FogPlayButton";
import { LightArcsFigure } from "../ui/LightArcsFigure";
import { NavLightsFigure } from "../ui/NavLightsFigure";
import { SemaphoreFigure } from "../ui/SemaphoreFigure";
import { TidalHeightsFigure } from "../ui/TidalHeightsFigure";

const NAV_LIGHTS_ASPECTS: ReadonlyArray<{ viewpoint: Viewpoint; label: string }> = [
  { viewpoint: "headOn", label: "Head-on" },
  { viewpoint: "stern", label: "Stern" },
  { viewpoint: "portBeam", label: "Port beam" },
  { viewpoint: "starboardBeam", label: "Stbd beam" },
];

interface ReferenceProps {
  onNavigate: (to: string) => void;
}

export type Section =
  | "morse"
  | "semaphore"
  | "flags"
  | "combos"
  | "nav-lights"
  | "day-shapes"
  | "buoyage"
  | "port-signals"
  | "lights-notation"
  | "irpcs"
  | "weather"
  | "distress"
  | "nato"
  | "chartwork"
  | "compass"
  | "astro"
  | "knots"
  | "anchoring"
  | "gps"
  | "tides"
  | "pilotage"
  | "sailing"
  | "mob"
  | "engine"
  | "mnemonics"
  | "comms"
  | "safety"
  | "predeparture"
  | "powerboat"
  | "passage"
  | "ropework"
  | "tender";

/// Sections whose richer standalone page (interactive calculators,
/// full diagrams, expandable detail) lives outside Reference. Each
/// such section gets a "Open full <name> tools →" button rendered
/// after the inline reference content.
const FULL_TOOLS: Partial<Record<Section, { path: string; label: string }>> = {
  knots: { path: "/play/knots", label: "Open full knots guide" },
  anchoring: { path: "/play/anchoring", label: "Open full anchoring guide" },
  gps: { path: "/play/gps", label: "Open full GPS literacy guide" },
  tides: { path: "/play/tides", label: "Open tide calculators" },
  pilotage: { path: "/play/pilotage", label: "Open pilotage tools" },
};

const SECTIONS: ReadonlyArray<{ id: Section; label: string }> = [
  { id: "morse", label: "Morse code" },
  { id: "semaphore", label: "Semaphore" },
  { id: "flags", label: "ICS flags" },
  { id: "combos", label: "Flag combinations" },
  { id: "nav-lights", label: "Nav lights" },
  { id: "day-shapes", label: "Day shapes" },
  { id: "buoyage", label: "IALA buoyage" },
  { id: "port-signals", label: "Port signals" },
  { id: "lights-notation", label: "Light notation" },
  { id: "irpcs", label: "IRPCS rules" },
  { id: "weather", label: "Met Office weather" },
  { id: "distress", label: "Distress signals" },
  { id: "nato", label: "NATO phonetic" },
  { id: "chartwork", label: "Chartwork" },
  { id: "compass", label: "Compass error" },
  { id: "astro", label: "Astronavigation" },
  { id: "knots", label: "Knots" },
  { id: "anchoring", label: "Anchoring" },
  { id: "gps", label: "GPS literacy" },
  { id: "tides", label: "Tides" },
  { id: "pilotage", label: "Pilotage" },
  { id: "sailing", label: "Sailing" },
  { id: "mob", label: "MOB recovery" },
  { id: "engine", label: "Engine — WOBBLE" },
  { id: "mnemonics", label: "Mnemonics" },
  { id: "comms", label: "VHF + SRC" },
  { id: "safety", label: "Safety + first aid" },
  { id: "predeparture", label: "Pre-departure + provisioning" },
  { id: "powerboat", label: "Powerboat handling" },
  { id: "passage", label: "Passage planning" },
  { id: "ropework", label: "Ropework + sail repair" },
  { id: "tender", label: "Tender handling" },
];

/// Single non-game lookup page. Aggregates the catalogue from every
/// module so a sailor can pull up the app at the chart table and
/// look something up without entering a flashcards session. Each
/// section is a quick filtered view; for deeper context (rule
/// citations, fog signals, etc.) the module-specific hubs still
/// own the full detail.
export function Reference({ onNavigate }: ReferenceProps) {
  const [section, setSection] = useState<Section>("morse");

  return (
    <>
      <p className="muted">
        Look-up reference for everything in the app — no game mode,
        no scoring. Use this on the boat when you need to remember
        what a flag means at a glance.
      </p>

      <style>{`
        .ref-picker-select { display: none; }
        .ref-picker-grid { display: flex; flex-wrap: wrap; gap: 6px; }
        @media (max-width: 768px) {
          .ref-picker-select { display: block; }
          .ref-picker-grid { display: none; }
        }
      `}</style>
      <div
        className="card"
        style={{ position: "sticky", top: 0, zIndex: 1 }}
      >
        {/* Narrow screens — native picker. Hands off to the OS list
            view on phones / tablet portrait so we don't burn 3 rows
            of vertical space on tap targets. */}
        <select
          className="ref-picker-select"
          value={section}
          onChange={(e) => setSection(e.target.value as Section)}
          aria-label="Reference section"
          style={{
            width: "100%",
            minHeight: 44,
            padding: "10px 12px",
            background: "var(--bg-elev)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {SECTIONS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>

        {/* Wider screens — wrap-grid of buttons. */}
        <div className="ref-picker-grid">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              className={section === s.id ? "" : "secondary"}
              onClick={() => setSection(s.id)}
              style={{ fontSize: 13, padding: "6px 10px" }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {section === "morse" && <MorseSection />}
      {section === "semaphore" && <SemaphoreSection />}
      {section === "flags" && <FlagsSection />}
      {section === "combos" && <CombosSection />}
      {section === "nav-lights" && <NavLightsSection />}
      {section === "day-shapes" && <DayShapesSection />}
      {section === "buoyage" && <BuoyageSection />}
      {section === "port-signals" && (
        <PortSignalsSection onNavigate={onNavigate} />
      )}
      {section === "lights-notation" && <LightsNotationSection />}
      {section === "irpcs" && <IrpcsSection />}
      {section === "anchoring" && <AnchoringSection />}
      {section === "gps" && <GpsSection />}
      {section === "tides" && <TidesSection />}
      {section === "pilotage" && <PilotageSection />}
      {section === "weather" && <WeatherSection />}
      {section === "distress" && <DistressSection />}
      {section === "nato" && <NatoSection />}
      {section === "chartwork" && <ChartworkSection />}
      {section === "compass" && <CompassSection />}
      {section === "astro" && <AstroSection />}
      {section === "knots" && <KnotsSection />}
      {section === "sailing" && <SailingSection />}
      {section === "mob" && <MobSection />}
      {section === "engine" && <EngineSection onNavigate={onNavigate} />}
      {section === "mnemonics" && <MnemonicsSection onNavigate={onNavigate} />}
      {section === "comms" && <CommsSection onNavigate={onNavigate} />}
      {section === "safety" && <SafetySection onNavigate={onNavigate} />}
      {section === "predeparture" && <PredeparturePage />}
      {section === "powerboat" && <PowerboatHandlingPage />}
      {section === "passage" && <PassagePlanningPage />}
      {section === "ropework" && <RopeworkPage />}
      {section === "tender" && <TenderPage />}

      {/* Reference-only modules have richer standalone pages (full
          diagrams, interactive calculators, expandable detail) that
          aren't reachable from Play any more. Link them out from
          their Reference tab so they stay discoverable. */}
      {FULL_TOOLS[section] && (
        <button
          onClick={() => onNavigate(FULL_TOOLS[section]!.path)}
          style={{ marginTop: 8 }}
        >
          {FULL_TOOLS[section]!.label} →
        </button>
      )}

      <button
        className="secondary"
        onClick={() => onNavigate("/play")}
        style={{ marginTop: 8 }}
      >
        ← Back to Play
      </button>
    </>
  );
}

export function MorseSection() {
  const [view, setView] = useState<"table" | "tree">("table");
  const groups: Array<{ title: string; chars: string }> = [
    { title: "Letters", chars: "ABCDEFGHIJKLMNOPQRSTUVWXYZ" },
    { title: "Digits", chars: "0123456789" },
    { title: "Punctuation", chars: ".,?/+-=" },
  ];
  return (
    <>
      <div className="row" style={{ gap: 8, marginBottom: 4 }}>
        <button
          className={view === "table" ? "" : "secondary"}
          onClick={() => setView("table")}
        >
          Table
        </button>
        <button
          className={view === "tree" ? "" : "secondary"}
          onClick={() => setView("tree")}
        >
          Tree
        </button>
      </div>

      {view === "tree" && <MorseTree />}

      {view === "table" &&
        groups.map((g) => (
        <div key={g.title} className="card">
          <h3>{g.title}</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
              gap: 8,
              marginTop: 8,
            }}
          >
            {g.chars.split("").map((c) => (
              <div
                key={c}
                style={{
                  textAlign: "center",
                  fontFamily: "ui-monospace, monospace",
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 600 }}>{c}</div>
                <div style={{ letterSpacing: "0.1em" }}>
                  {(ITU_CODE[c] ?? "").replace(/\./g, "•").replace(/-/g, "—")}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export function SemaphoreSection() {
  return (
    <>
      <div className="card">
        <h3>Letters</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
            gap: 8,
            marginTop: 8,
          }}
        >
          {Object.keys(SEMAPHORE_CODE).map((c) => (
            <div key={c} style={{ textAlign: "center" }}>
              <SemaphoreFigure pose={SEMAPHORE_CODE[c]!} size={80} />
              <div className="small muted" style={{ marginTop: 4 }}>
                {c}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <h3>Numerals (after the J "numbers follow" prefix)</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {Object.entries(SEMAPHORE_NUMERALS).map(([digit, letter]) => (
              <tr key={digit}>
                <td style={{ fontWeight: 600, padding: "2px 12px 2px 0" }}>
                  {digit}
                </td>
                <td className="muted">letter pose {letter}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function FlagsSection() {
  const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const DIGITS = "0123456789".split("");
  return (
    <>
      <div className="card">
        <h3>Letter flags</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Each letter flag has an operational meaning when flown
          alone. Several letters carry an additional meaning when
          made by fishing vessels operating in close proximity on
          the fishing grounds.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 16,
            marginTop: 8,
          }}
        >
          {LETTERS.map((c) => {
            const spec = ICS_FLAGS[c];
            return (
              <div
                key={c}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 4,
                }}
              >
                <Flag char={c} size={64} />
                <div
                  className="small"
                  style={{ marginTop: 4, fontWeight: 700 }}
                >
                  {c} — {spec?.name}
                </div>
                {spec?.meaning && (
                  <div
                    className="small muted"
                    style={{
                      fontSize: 12,
                      lineHeight: 1.35,
                      marginTop: 2,
                    }}
                  >
                    {spec.meaning}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="card">
        <h3>Numeric pennants</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Each digit has TWO accepted radio-spoken forms:{" "}
          <strong>IMO</strong> (SMCP-standard maritime — most
          shore-side traffic) and <strong>NATO</strong> (military
          phonetic, still used by many vessels).
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 12,
            marginTop: 8,
          }}
        >
          {DIGITS.map((c) => {
            const spec = ICS_FLAGS[c];
            return (
              <div
                key={c}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 4,
                }}
              >
                <Flag char={c} size={56} />
                <div
                  className="small"
                  style={{ marginTop: 4, fontWeight: 700, fontSize: 16 }}
                >
                  {c}
                </div>
                {spec?.pronunciation && (
                  <div
                    className="small muted"
                    style={{ fontSize: 11, lineHeight: 1.35 }}
                  >
                    <span style={{ opacity: 0.7 }}>IMO:</span>{" "}
                    <em>{spec.pronunciation}</em>
                  </div>
                )}
                {spec?.natoPronunciation && (
                  <div
                    className="small muted"
                    style={{ fontSize: 11, lineHeight: 1.35 }}
                  >
                    <span style={{ opacity: 0.7 }}>NATO:</span>{" "}
                    {spec.natoPronunciation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* NATO numeric flags — the military counterpart of the ICS
          pennants. Both sets are recognised at sea; many vessels
          carry both. */}
      <div className="card">
        <h3>NATO numeric flags (alternative)</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          NATO Allied Tactical Publication 1 uses RECTANGULAR
          numeral flags (no pennant taper, no swallowtail), distinct
          from the ICS pennants above. Same digits — same spoken
          forms — different visual designs. Modern naval vessels
          typically carry both sets.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: 12,
            marginTop: 8,
          }}
        >
          {DIGITS.map((c) => {
            const id = `N${c}`;
            const spec = ICS_FLAGS[id];
            if (!spec) return null;
            return (
              <div
                key={id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 4,
                }}
              >
                <Flag char={id} size={56} />
                <div
                  className="small"
                  style={{ marginTop: 4, fontWeight: 700, fontSize: 16 }}
                >
                  {c}
                </div>
                <div className="small muted" style={{ fontSize: 11 }}>
                  NATO
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Operational / special flags — answering pennant + the three
          substitutes. Not part of A-Z or 0-9, but every skipper
          should recognise them in a hoist. */}
      <div className="card">
        <h3>Special flags</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Operational signals — not letters or digits, but commonly
          flown with them. The substitutes repeat earlier flags of the
          same class in a hoist so you don't need duplicate copies.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 12,
            marginTop: 8,
          }}
        >
          {(["ANSWERING", "SUB1", "SUB2", "SUB3"] as const).map((id) => {
            const spec = ICS_FLAGS[id];
            if (!spec) return null;
            return (
              <div key={id} style={{ textAlign: "center" }}>
                <Flag char={id} size={72} />
                <div
                  className="small"
                  style={{ marginTop: 4, fontWeight: 600 }}
                >
                  {spec.name}
                </div>
                {spec.shortMeaning && (
                  <div
                    className="small muted"
                    style={{ fontSize: 11, lineHeight: 1.3 }}
                  >
                    {spec.shortMeaning}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function CombosSection() {
  return (
    <div className="card">
      <h3>Two-flag combinations</h3>
      <p className="small muted">
        Most common IRPCS / ICS Volume I composite signals.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 12,
          marginTop: 8,
        }}
      >
        {Object.values(ICS_COMBOS).map((c) => (
          <div key={c.id} style={{ textAlign: "center" }}>
            <FlagHoist flags={c.flags} size={56} />
            <div className="small" style={{ marginTop: 6, fontWeight: 600 }}>
              {c.id}
            </div>
            <div className="small muted">{c.shortMeaning}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function NavLightsSection() {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Arcs of visibility (Rule 21)</h3>
        <div
          style={{
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <LightArcsFigure size={220} />
          <ul
            className="small muted"
            style={{ margin: 0, paddingLeft: 18, flex: "1 1 220px" }}
          >
            <li>
              <strong>Masthead</strong> 225° — right ahead to 22.5°
              abaft each beam (Rule 21(a)).
            </li>
            <li>
              <strong>Sidelights</strong> 112.5° each — ahead to
              22.5° abaft the beam on their respective side
              (Rule 21(b)).
            </li>
            <li>
              <strong>Sternlight</strong> 135° — 67.5° each side of
              dead astern (Rule 21(c)). Same arc as a yellow towing
              light (Rule 21(d)).
            </li>
            <li>
              At the exact beam, you see masthead + same-side
              sidelight only. Sternlight starts 22.5° abaft beam —
              not visible from the beam itself.
            </li>
            <li>
              All-round lights (anchor, NUC, RAM, fishing, draft,
              pilot, mine-clearance, hovercraft yellow) cover the
              full 360°.
            </li>
          </ul>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Underway vs making way</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          A vessel is <strong>under way</strong> when she is not at
          anchor, not made fast to the shore, and not aground (Rule
          3(i)). She is <strong>making way</strong> when she is{" "}
          <em>also</em> moving through the water.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          The distinction matters for lights: <strong>NUC</strong>,{" "}
          <strong>RAM</strong>, <strong>fishing</strong>, and{" "}
          <strong>mine-clearance</strong> vessels add sidelights and
          a sternlight <em>only when making way</em>. The figures
          below always show the making-way case; each card's
          "Making way / underway" note lists what disappears when the
          vessel is stopped.
        </p>
      </div>

      {VESSEL_IDS.map((id) => {
        const v = NAV_LIGHTS[id]!;
        return (
          <div key={id} className="card">
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{v.name}</div>
            <div className="small muted" style={{ marginBottom: 12 }}>
              {v.description}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: 8,
                rowGap: 12,
              }}
            >
              {NAV_LIGHTS_ASPECTS.map((a) => (
                <div
                  key={a.viewpoint}
                  style={{
                    gridColumn: "1 / -1",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <NavLightsFigure
                      vessel={v}
                      viewpoint={a.viewpoint}
                      size={108}
                    />
                    <div className="small muted">{a.label} — night</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <DaytimeVesselFigure
                      vessel={v}
                      viewpoint={a.viewpoint}
                      size={108}
                    />
                    <div className="small muted">{a.label} — day</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="small muted" style={{ margin: "12px 0 0" }}>
              <strong>Rule:</strong> {v.rule}
            </p>
            {v.makesWayNote && (
              <p className="small muted" style={{ margin: "4px 0 0" }}>
                <strong>Making way / underway:</strong> {v.makesWayNote}
              </p>
            )}
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              <strong>Fog signal:</strong> {v.fogSignal}
            </p>
            <FogPlayButton pattern={v.fogPattern} />
          </div>
        );
      })}
    </>
  );
}

export function IrpcsSection() {
  const pecking = findMnemonic("irpcs-pecking-order");
  return (
    <>
      <p className="small muted">
        Part B steering and sailing rules plus the most-quoted signal
        rules from Parts C and D. Tap a rule to expand.
      </p>
      {pecking && <MnemonicCard m={pecking} />}
      {IRPCS_RULES.map((r) => (
        <details key={r.number} className="card">
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            Rule {r.number} — {r.title}
            <div
              className="small muted"
              style={{ marginTop: 2, fontWeight: 400 }}
            >
              {r.summary}
            </div>
          </summary>
          <ul style={{ marginTop: 12, paddingLeft: 18 }}>
            {r.keyPoints.map((k, i) => (
              <li key={i} className="small" style={{ marginBottom: 4 }}>
                {k}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </>
  );
}

export function PilotageSection() {
  return (
    <>
      <ThreePointFixCalculator />
      <AdvancedFixesPanel />
      <div className="card">
        <h3>Pilotage techniques</h3>
      <ul style={{ paddingLeft: 18, margin: 0 }}>
        {TECHNIQUE_IDS.map((id) => {
          const t = TECHNIQUES[id]!;
          return (
            <li key={id} className="small" style={{ marginBottom: 6 }}>
              <strong>{t.name}</strong>{" "}
              <span className="muted">— {t.summary}</span>
            </li>
          );
        })}
        </ul>
      </div>
    </>
  );
}

export function TidesSection() {
  const rot = findMnemonic("rule-of-twelfths");
  return (
    <>
      {rot && <MnemonicCard m={rot} />}
      <RuleOfTwelfthsCalculator />
      <SecondaryPortCalculator />
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Which datum is the chart figure from?</h3>
        <p className="small muted" style={{ marginBottom: 10 }}>
          Every figure printed on an Admiralty chart is measured
          either from <strong>MHWS</strong> (heights — bridges,
          lights, hilltops) or <strong>Chart Datum</strong> (depths
          and drying heights). The arrows below pair each chart
          annotation with the datum it's referenced to.
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ChartDatumsFigure size={620} />
        </div>
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Tide-level cross-section</h3>
        <p className="small muted" style={{ marginBottom: 10 }}>
          Eight horizontal lines mark the standard tidal levels; the
          blue line marks an example "now" tide; arrows show charted
          depth, height of tide, sounding, drying height, and
          vertical clearance.
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <TidalHeightsFigure size={620} />
        </div>
      </div>
      <div className="card">
        <h3>Tidal levels</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {TIDAL_LEVELS.map((l) => (
              <tr key={l.abbreviation} style={{ verticalAlign: "top" }}>
                <td
                  style={{
                    padding: "6px 8px 6px 0",
                    width: 70,
                    fontWeight: 700,
                  }}
                >
                  {l.abbreviation}
                </td>
                <td style={{ padding: "6px 8px" }}>
                  <div style={{ fontWeight: 600 }}>{l.name}</div>
                  <div className="small muted">{l.description}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h3>Measurements</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {TIDAL_MEASUREMENTS.map((m) => (
              <tr key={m.id} style={{ verticalAlign: "top" }}>
                <td
                  style={{
                    padding: "6px 8px 6px 0",
                    width: "30%",
                    fontWeight: 600,
                  }}
                >
                  {m.name}
                </td>
                <td className="muted" style={{ padding: "6px 8px" }}>
                  {m.definition}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TidalStreamsPanel />
      <XtePanel />
    </>
  );
}

export function CompassSection() {
  const cadet = findMnemonic("cadet");
  return (
    <>
      {cadet && <MnemonicCard m={cadet} />}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Compass, magnetic & true</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Three norths, two corrections. Read <strong>variation</strong> off
          the chart's compass rose; read <strong>deviation</strong> off the
          boat's deviation card (it changes with heading). The CADET mnemonic
          fixes which way the signs run.
        </p>
        <CompassCadetDiagram />
      </div>

      <CompassCalculator />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Example deviation card</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          Deviation isn't a single number — it varies with the vessel's
          heading. Read the row nearest your compass heading.
        </p>
        <table style={{ width: "100%", fontSize: 14, maxWidth: 320 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Heading</th>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Deviation</th>
            </tr>
          </thead>
          <tbody>
            {SAMPLE_DEVIATION_CARD.map((row) => (
              <tr key={row.headingDeg}>
                <td style={{ padding: "4px 8px", fontWeight: 600 }}>
                  {formatHeading(row.headingDeg)}°
                </td>
                <td className="muted" style={{ padding: "4px 8px" }}>
                  {formatCompassError(row.deviation)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function GpsSection() {
  return (
    <>
      <div className="card">
        <h3>Track-vector terms</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {TRACK_TERMS.map((t) => (
              <tr key={t.abbreviation}>
                <td
                  style={{
                    padding: "4px 8px 4px 0",
                    fontWeight: 700,
                    width: 60,
                  }}
                >
                  {t.abbreviation}
                </td>
                <td style={{ padding: "4px 8px" }}>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div className="small muted">{t.meaning}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h3>Datums</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {DATUMS.map((d) => (
              <tr key={d.abbreviation}>
                <td
                  style={{
                    padding: "4px 8px 4px 0",
                    fontWeight: 700,
                    width: 80,
                  }}
                >
                  {d.abbreviation}
                </td>
                <td className="muted" style={{ padding: "4px 8px" }}>
                  {d.region} — {d.shiftFromWgs84}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function AnchoringSection() {
  return (
    <>
      <BerthingTechniquesPanel />
      <AnchorTechniquesPanel />
      <div className="card">
        <h3>Scope</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              <th style={{ padding: "4px 8px 4px 0" }}>Rode</th>
              <th style={{ padding: "4px 8px" }}>Settled</th>
              <th style={{ padding: "4px 8px" }}>Normal</th>
              <th style={{ padding: "4px 8px" }}>Heavy</th>
            </tr>
          </thead>
          <tbody>
            {SCOPE_GUIDELINES.map((s) => (
              <tr key={s.rode}>
                <td style={{ padding: "4px 8px 4px 0", fontWeight: 600 }}>
                  {s.rodeLabel}
                </td>
                <td style={{ padding: "4px 8px" }}>{s.minimum}</td>
                <td style={{ padding: "4px 8px" }}>{s.normal}</td>
                <td style={{ padding: "4px 8px" }}>{s.heavy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h3>Anchor types</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 16,
            marginTop: 8,
          }}
        >
          {ANCHOR_IDS.map((id) => {
            const a = ANCHORS[id]!;
            return (
              <div
                key={id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  padding: 8,
                  borderRadius: 6,
                  background: "var(--accent-soft, #f5f1e6)",
                }}
              >
                <AnchorFigure anchorId={id} size={140} />
                <div style={{ fontWeight: 600, marginTop: 4 }}>{a.name}</div>
                <div className="small muted" style={{ marginTop: 2 }}>
                  {a.summary}
                </div>
              </div>
            );
          })}
        </div>
        <p className="small muted" style={{ margin: "12px 0 0" }}>
          Delta and Manson Supreme are shown as stylised diagrams — no
          suitable Commons photo yet. All other photos are from
          Wikimedia Commons under the licences listed below; custom
          replacements are on the roadmap.
        </p>
      </div>
      <details className="card">
        <summary
          style={{
            cursor: "pointer",
            listStyle: "none",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Photo credits — Wikimedia Commons
        </summary>
        <ul
          className="small muted"
          style={{ paddingLeft: 18, margin: "8px 0 0" }}
        >
          {ANCHOR_PHOTO_CREDITS.map((c) => (
            <li key={c.anchorId} style={{ marginBottom: 4 }}>
              <strong>{ANCHORS[c.anchorId]?.name ?? c.anchorId}</strong>{" "}
              — {c.credit} ·{" "}
              <a href={c.source} target="_blank" rel="noopener noreferrer">
                {c.licence}
              </a>
            </li>
          ))}
        </ul>
      </details>
      <div className="card">
        <h3>Holding ground</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {HOLDING_GROUND_IDS.map((id) => {
              const g = HOLDING_GROUNDS[id]!;
              return (
                <tr key={id}>
                  <td style={{ padding: "4px 8px 4px 0", fontWeight: 600 }}>
                    {g.name}
                  </td>
                  <td className="muted" style={{ padding: "4px 8px", width: 60 }}>
                    {g.chartAbbreviation ?? "—"}
                  </td>
                  <td className="muted" style={{ padding: "4px 8px" }}>
                    {g.rating}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function BuoyageSection() {
  const [region, setRegion] = useState<Region>("A");
  return (
    <>
      <RegionGeographyPanel />
      <RegionDifferencesPanel />
      <IwwMarksPanel />
      <div className="card">
        <div className="row" style={{ gap: 8 }}>
          {REGIONS.map((r) => (
            <button
              key={r}
              className={region === r ? "" : "secondary"}
              onClick={() => setRegion(r)}
            >
              Region {r}
            </button>
          ))}
        </div>
        <p className="small muted" style={{ margin: "8px 0 0" }}>
          {regionLabel(region)}
        </p>
      </div>
      <div className="card">
        <h3>Marks</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 16,
            marginTop: 8,
          }}
        >
          {IALA_IDS.map((id) => {
            const m = IALA_MARKS[id]!;
            // Fixed-height rows keep every cell on the same vertical
            // baseline, so a two-line name on one column doesn't shove
            // the next column's text down.
            return (
              <div
                key={id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 4,
                }}
              >
                <BuoyFigure mark={m} region={region} size={90} />
                <div
                  className="small"
                  style={{
                    marginTop: 4,
                    fontWeight: 600,
                    minHeight: 34,
                    lineHeight: 1.25,
                  }}
                >
                  {m.name}
                </div>
                <div
                  className="small muted"
                  style={{ minHeight: 30, lineHeight: 1.25 }}
                >
                  {m.shortMeaning}
                </div>
                <div
                  className="small muted"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginTop: 2,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 11,
                  }}
                >
                  <LightRhythm mark={m} region={region} size={10} />
                  {lightForRegion(m, region).abbreviation}
                </div>
              </div>
            );
          })}
        </div>
        <p className="small muted" style={{ marginTop: 12, marginBottom: 0 }}>
          Spec source: Trinity House —{" "}
          <a
            href="https://www.trinityhouse.co.uk/commercial-services/our-services/buoy-services/buoy-rental"
            target="_blank"
            rel="noopener noreferrer"
          >
            trinityhouse.co.uk
          </a>
          .
        </p>
      </div>
    </>
  );
}

/// International Port Traffic Signals — IALA Recommendation R0111.
/// Same source data as the Pilotage tools page; rendered here in the
/// IALA-marks style with animated bulbs so the difference between
/// Signal 1 (3 flashing reds) and Signal 2 (3 fixed reds) is
/// immediately obvious from the rhythm alone.
export function PortSignalsSection({ onNavigate }: { onNavigate?: (to: string) => void } = {}) {
  const mainSignals = PORT_SIGNALS.filter((s) => s.kind === "main");
  const supplementarySignals = PORT_SIGNALS.filter(
    (s) => s.kind === "supplementary",
  );

  return (
    <>
      <div className="card">
        <p className="muted small">
          The standard five-signal IPTS scheme used at most commercial
          harbours worldwide. Always check the local almanac — many
          ports add or modify supplementary signals.
        </p>
        <p className="muted small" style={{ marginTop: 8 }}>
          Mast carries three vertical lights for the main signals,
          read top-to-bottom; supplementary lights sit on a smaller
          slim mast to the RIGHT of the main signal.
        </p>
      </div>

      <div className="card">
        <h3>Main signals</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            gap: 16,
            marginTop: 8,
          }}
        >
          {mainSignals.map((s, i) => (
            <div
              key={s.id}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <SignalStack signal={s} size={90} />
              <div
                className="small"
                style={{ marginTop: 6, fontWeight: 700, minHeight: 22 }}
              >
                Signal {i + 1}
              </div>
              <div
                className="small"
                style={{ fontWeight: 600, minHeight: 40, lineHeight: 1.25 }}
              >
                {s.shortMeaning}
              </div>
              <div className="small muted" style={{ marginTop: 4 }}>
                {s.meaning}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>Supplementary</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
          Side-lamps shown alongside a main signal to modify its
          meaning.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            gap: 16,
          }}
        >
          {supplementarySignals.map((s) => (
            <div
              key={s.id}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <SignalStack signal={s} size={90} />
              <div
                className="small"
                style={{ marginTop: 6, fontWeight: 600, minHeight: 40, lineHeight: 1.25 }}
              >
                {s.shortMeaning}
              </div>
              <div className="small muted" style={{ marginTop: 4 }}>
                {s.meaning}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="small muted" style={{ marginTop: 8, marginBottom: 0 }}>
        Source: IALA Recommendation (Normative) R0111 (E-111) — Port
        Traffic Signals, Edition 1.3
        (<code>urn:mrn:iala:pub:r0111:ed1.3</code>). Signals 2–5 are
        shown fixed but the spec allows "fixed or slow-occulting
        synchronised". A useful summary is at{" "}
        <a
          href="http://www.sailtrain.co.uk/pilotage/traffic.htm"
          target="_blank"
          rel="noopener noreferrer"
        >
          sailtrain.co.uk
        </a>{" "}
        — open the full{" "}
        {onNavigate ? (
          <a
            href="/play/pilotage"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("/play/pilotage");
            }}
          >
            Pilotage tools page
          </a>
        ) : (
          <span>Pilotage tools page</span>
        )}{" "}
        for the broader pilotage context (transits, leading lines,
        danger bearings, harbour-entry pre-flight).
      </p>
    </>
  );
}

/// Decoder ring for Admiralty / IALA light-rhythm abbreviations.
/// Every value in `mark.light.abbreviation` across the IALA package
/// is covered here — adding a new mark with a novel abbreviation
/// should be paired with a row in this table.
export function LightsNotationSection() {
  const rows: ReadonlyArray<{
    abbr: string;
    name: string;
    meaning: string;
    examples: string;
  }> = [
    {
      abbr: "Fl",
      name: "Flashing",
      meaning:
        "Single short flash, repeating. Duration of darkness exceeds duration of light. A bare 'Fl' has no group / period — typical of lateral marks.",
      examples: "Port-hand, starboard-hand lateral marks",
    },
    {
      abbr: "Fl(2+1)",
      name: "Composite group flashing",
      meaning:
        "Two flashes, short gap, one flash, longer gap. Repeats. The composite grouping marks a divided channel.",
      examples: "Preferred-channel marks (Fl(2+1).R / Fl(2+1).G)",
    },
    {
      abbr: "Fl(2)",
      name: "Group flashing",
      meaning:
        "A fixed number of flashes repeating per period. Fl(2)5s = two flashes every 5 seconds.",
      examples: "Isolated-danger mark (Fl(2) 5s)",
    },
    {
      abbr: "Q",
      name: "Quick",
      meaning:
        "About 50–79 flashes per minute (≈ 1 per second). Used for cardinal marks.",
      examples: "Cardinals — N: continuous Q; E: Q(3) 10s; etc.",
    },
    {
      abbr: "VQ",
      name: "Very quick",
      meaning:
        "About 100–159 flashes per minute (≈ 2 per second). Used interchangeably with Q on cardinals, with a shorter period.",
      examples: "Cardinals — VQ(3) 5s / VQ(6)+LFl 10s / VQ(9) 10s",
    },
    {
      abbr: "Q(3), Q(6), Q(9)",
      name: "Group quick",
      meaning:
        "Three / six / nine quick flashes per period. The count maps to the cardinal: E=3, S=6+LFl, W=9.",
      examples: "East, south, west cardinals (mnemonic: clock-face hours)",
    },
    {
      abbr: "LFl",
      name: "Long flash",
      meaning:
        "Single flash of at least 2 seconds. Appears on its own or as the trailing element of a south-cardinal Q(6)+LFl.",
      examples: "Safe water (one rhythm), south cardinal trailer",
    },
    {
      abbr: "Iso",
      name: "Isophase",
      meaning:
        "Equal duration of light and darkness. Used on some safe-water marks.",
      examples: "Safe water (one of four permitted rhythms)",
    },
    {
      abbr: "Oc",
      name: "Occulting",
      meaning:
        "Light is on more than it's off. Brief eclipses in an otherwise-steady light.",
      examples: "Safe water (one of four permitted rhythms)",
    },
    {
      abbr: "Mo(A)",
      name: "Morse code letter",
      meaning:
        "Light flashes the Morse pattern for the letter in brackets. Mo(A) = short-long (• —), repeating with a longer gap between repetitions.",
      examples: "Safe water (one of four permitted rhythms)",
    },
    {
      abbr: "Al.BuY",
      name: "Alternating colours",
      meaning:
        "Light switches between two colours — here blue and yellow — with brief darkness between each. Distinctive enough to be unmistakable at any distance.",
      examples: "Emergency wreck-marking buoy (Al.BuY 3s)",
    },
    {
      abbr: ".R / .G / .Y / .Bu / .W",
      name: "Colour suffix",
      meaning:
        "Tells you the light's colour. Red / Green / Yellow / Blue / White. No suffix = white by default.",
      examples: "Fl.R = flashing red; Fl(2+1).G = composite-group green",
    },
    {
      abbr: "<n>s",
      name: "Period",
      meaning:
        "Trailing seconds = full cycle length. Fl(2)5s means the full sequence (two flashes + the dark gap before it repeats) takes 5 seconds total.",
      examples: "VQ(3) 5s, Q(9) 15s, Al.BuY 3s",
    },
  ];

  return (
    <>
      <div className="card">
        <p className="muted">
          Light characteristics shown on Admiralty charts, in the
          UKHO Light List, and in this app follow a compact notation.
          Each line below decodes one piece of that notation.
        </p>
        <p className="small muted" style={{ marginTop: 8 }}>
          Compose them right-to-left: <em>Q(3) 10s</em> = group quick,
          three flashes, every 10 seconds.
        </p>
      </div>

      <div className="card">
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              <th style={{ padding: "6px 8px 6px 0", width: "20%" }}>Notation</th>
              <th style={{ padding: "6px 8px", width: "20%" }}>Name</th>
              <th style={{ padding: "6px 8px" }}>What it means</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.abbr}
                style={{
                  verticalAlign: "top",
                  borderTop: "1px solid var(--border)",
                }}
              >
                <td
                  style={{
                    padding: "8px 8px 8px 0",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontWeight: 700,
                  }}
                >
                  {r.abbr}
                </td>
                <td style={{ padding: "8px", fontWeight: 600 }}>{r.name}</td>
                <td className="muted" style={{ padding: "8px" }}>
                  <div>{r.meaning}</div>
                  <div className="small" style={{ marginTop: 4, opacity: 0.75 }}>
                    <strong>Used by:</strong> {r.examples}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="small muted" style={{ marginTop: 8, marginBottom: 0 }}>
        See the IALA buoyage tab for every mark's specific
        characteristic. The animated dot next to each mark there
        shows the rhythm in real time.
      </p>

      {/* ────────────────────────────────────────────────────────
          Sector lights — different rhythm/colour by bearing sector.
          Common on harbour-approach lights where a single light
          tells you on which bearing the safe lead-in lies.
      ──────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 16 }}>
        <h3>Sector lights</h3>
        <p className="muted small">
          A single light showing different characteristics depending on
          your bearing to it. Almanac and chart spell out the safe
          sector first, then the danger sectors with their bearing
          limits.
        </p>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <tbody>
            <tr style={{ verticalAlign: "top" }}>
              <td
                style={{
                  padding: "6px 8px 6px 0",
                  width: 140,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 700,
                }}
              >
                Oc.WRG.10s
              </td>
              <td className="muted" style={{ padding: "6px 8px" }}>
                Occulting White (safe sector), Red and Green (danger
                sectors), 10-second period.
              </td>
            </tr>
            <tr
              style={{
                verticalAlign: "top",
                borderTop: "1px solid var(--border)",
              }}
            >
              <td
                style={{
                  padding: "8px 8px 8px 0",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 700,
                }}
              >
                Fl.WR.5s
              </td>
              <td className="muted" style={{ padding: "8px" }}>
                Flashing white in the clear sector, red in the danger
                sector, 5-second period.
              </td>
            </tr>
          </tbody>
        </table>
        <p className="small muted" style={{ marginTop: 8 }}>
          Sector boundaries are given as <strong>charted bearings</strong>{" "}
          from your vessel to the light. White = approach is safe; red =
          danger (typically to starboard of the safe sector when looking
          from sea); green = danger to port. Stay inside the white sector
          until close enough to switch to the next leg.
        </p>
      </div>

      {/* ────────────────────────────────────────────────────────
          Leading lights — a synchronised pair forming a safe
          transit. Front lower, rear higher; align them vertically
          and you're on the safe line into the harbour.
      ──────────────────────────────────────────────────────── */}
      <div className="card">
        <h3>Leading lights</h3>
        <p className="muted small">
          A pair of lights — front (lower) + rear (higher) — establishing
          a safe transit into a harbour. When the lights stack vertically
          you're on the leading line; if the rear slides to one side,
          alter course towards the rear to bring them back into line.
        </p>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <tbody>
            <tr style={{ verticalAlign: "top" }}>
              <td
                style={{
                  padding: "6px 8px 6px 0",
                  width: 140,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 700,
                }}
              >
                Ldg Lts
              </td>
              <td className="muted" style={{ padding: "6px 8px" }}>
                Leading lights — pair forming a transit. Often labelled
                with the lead-in bearing, e.g. <em>Ldg 235°</em>.
              </td>
            </tr>
            <tr
              style={{
                verticalAlign: "top",
                borderTop: "1px solid var(--border)",
              }}
            >
              <td
                style={{
                  padding: "8px 8px 8px 0",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 700,
                }}
              >
                F.R / F.G
              </td>
              <td className="muted" style={{ padding: "8px" }}>
                Fixed Red, Fixed Green — the most common leading-light
                colours. Many pairs are synchronised flashing instead
                (Fl.R or Oc.W) so they stand out against urban
                light pollution.
              </td>
            </tr>
            <tr
              style={{
                verticalAlign: "top",
                borderTop: "1px solid var(--border)",
              }}
            >
              <td
                style={{
                  padding: "8px 8px 8px 0",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 700,
                }}
              >
                Iso.R sync
              </td>
              <td className="muted" style={{ padding: "8px" }}>
                Isophase red lights, time-synced — both lights brighten
                and dim together so the eye sees one apparent flashing
                target on the leading line.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ────────────────────────────────────────────────────────
          Full lighthouse characteristic — rhythm + colour + period
          + elevation + nominal range, in that order.
      ──────────────────────────────────────────────────────── */}
      <div className="card">
        <h3>Lighthouse characteristic</h3>
        <p className="muted small">
          A full chart entry stacks the rhythm with elevation + range.
          Read it as five fields:
        </p>
        <p
          style={{
            margin: "12px 0",
            padding: "12px",
            background: "var(--bg-card-soft, #f6f8fb)",
            borderRadius: 8,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 16,
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          Fl(2)W.10s 45m 20M
        </p>
        <ul
          className="small muted"
          style={{ paddingLeft: 20, marginTop: 8, marginBottom: 0 }}
        >
          <li>
            <strong>Fl(2)</strong> — group flashing, two flashes per period
          </li>
          <li>
            <strong>W</strong> — colour: White (R/G/Y/Bu also possible)
          </li>
          <li>
            <strong>10s</strong> — period: 10 seconds for the full cycle
          </li>
          <li>
            <strong>45m</strong> — elevation: height of the light above
            Mean High Water Springs (MHWS), in metres
          </li>
          <li>
            <strong>20M</strong> — nominal range: distance the light is
            visible in 10-NM met visibility, in nautical miles
          </li>
        </ul>
      </div>

      {/* ────────────────────────────────────────────────────────
          Range of a light — the two-limit explanation + a small
          calculator for the geographic horizon.
      ──────────────────────────────────────────────────────── */}
      <div className="card">
        <h3>Range — how far can you see the light?</h3>
        <p className="muted small">
          Two separate things limit when a light becomes visible to you,
          and you only see it when both clear:
        </p>
        <ul className="small muted" style={{ paddingLeft: 20, marginTop: 4 }}>
          <li>
            <strong>Nominal range</strong> (printed on the chart as e.g.{" "}
            <code>20M</code>) — how far the lamp can throw its beam in
            standard 10-NM meteorological visibility. Set by the light's
            wattage + lens.
          </li>
          <li>
            <strong>Geographic range</strong> — your physical horizon
            limit, dictated by Earth's curvature based on both the light's
            height and your eye height above the water. The taller the
            light, the further over the horizon you can see it; the
            taller your viewpoint, the further your horizon reaches.
          </li>
        </ul>
        <p className="muted small" style={{ marginTop: 8 }}>
          The visible range is the <strong>smaller</strong> of the two.
          For a tall lighthouse the geographic range is usually the
          limit; for a buoy the nominal range often is.
        </p>
        <p
          style={{
            margin: "12px 0 4px",
            padding: "10px 12px",
            background: "var(--bg-card-soft, #f6f8fb)",
            borderRadius: 8,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 14,
            textAlign: "center",
          }}
        >
          range<sub>NM</sub> = 2.08 × ( √h<sub>obs (m)</sub> +
          √h<sub>light (m)</sub> )
        </p>
        <p className="small muted" style={{ marginTop: 4, marginBottom: 12 }}>
          Example: a 25 M lighthouse at 60 m elevation, viewed from 2 m
          above the sea, has geographic range = 2.08 × (√2 + √60) =
          2.08 × (1.41 + 7.75) ≈ 19 M. The 25 M nominal can't help —
          you'll see it first at about 19 M as the lamp climbs above
          your horizon (the so-called <em>rising of the light</em>).
        </p>

        <LightRangeCalculator />
      </div>
    </>
  );
}

/// Inline calculator for the geographic + effective range of a light.
/// Takes observer eye height, light's elevation, and the charted
/// nominal range; reports both ranges and the limiting one.
function LightRangeCalculator() {
  const [obsHeightM, setObsHeightM] = useState("2");
  const [lightHeightM, setLightHeightM] = useState("30");
  const [nominalNM, setNominalNM] = useState("20");

  const h_obs = parseFloat(obsHeightM);
  const h_light = parseFloat(lightHeightM);
  const nominal = parseFloat(nominalNM);
  const valid =
    Number.isFinite(h_obs) &&
    h_obs >= 0 &&
    Number.isFinite(h_light) &&
    h_light >= 0 &&
    Number.isFinite(nominal) &&
    nominal >= 0;
  const geo = valid ? 2.08 * (Math.sqrt(h_obs) + Math.sqrt(h_light)) : null;
  const effective = valid && geo !== null ? Math.min(geo, nominal) : null;
  const limited = valid && geo !== null && nominal < geo;

  return (
    <div
      className="card"
      style={{
        marginTop: 8,
        background: "var(--bg-card-soft, #f6f8fb)",
        padding: 12,
      }}
    >
      <h4 style={{ marginTop: 0, marginBottom: 8 }}>Visible-range calculator</h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 8,
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="small muted">Observer height (m)</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.5"
            value={obsHeightM}
            onChange={(e) => setObsHeightM(e.target.value)}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="small muted">Light elevation (m)</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={lightHeightM}
            onChange={(e) => setLightHeightM(e.target.value)}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="small muted">Nominal range (M)</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={nominalNM}
            onChange={(e) => setNominalNM(e.target.value)}
          />
        </label>
      </div>
      {valid && geo !== null && effective !== null && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 6,
            background: "rgba(31, 111, 165, 0.10)",
          }}
        >
          <p className="small" style={{ margin: 0 }}>
            <strong>Geographic range:</strong> {geo.toFixed(1)} M
          </p>
          <p className="small" style={{ margin: "2px 0" }}>
            <strong>Nominal range:</strong> {nominal.toFixed(1)} M
          </p>
          <p
            className="small"
            style={{
              margin: "4px 0 0",
              fontWeight: 700,
              color: "var(--accent, #1f6fa5)",
            }}
          >
            Visible at ≈ {effective.toFixed(1)} M{" "}
            <span className="muted" style={{ fontWeight: 400 }}>
              ({limited ? "nominal-range-limited" : "horizon-limited"})
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

/// Day shapes are shared across vessel sizes — both anchored<50m
/// and anchored≥50m carry the same single ball; both trawler and
/// "fishing other than trawling" share the basic two-cones diamond
/// (Rule 26(a)). Group by the dayShapes signature so each unique
/// silhouette only appears once, with all vessel names that share
/// it listed together.
export function DayShapesSection() {
  const groups = groupByDayShapes();
  return (
    <div className="card">
      <h3>Day shapes</h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
          gap: 16,
          marginTop: 8,
        }}
      >
        {groups.map((g) => (
          <div key={g.signature} style={{ textAlign: "center" }}>
            <DayShapeFigure
              shapes={g.shapes}
              {...(g.obstructionSide
                ? { obstructionSide: g.obstructionSide }
                : {})}
              {...(g.safeSide ? { safeSide: g.safeSide } : {})}
              size={90}
            />
            <div className="small" style={{ marginTop: 6, fontWeight: 600 }}>
              {g.vessels.join(" · ")}
            </div>
            <div className="small muted">{g.rule}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface DayShapeGroup {
  signature: string;
  shapes: readonly import("@nav-games/nav-lights").DayShape[];
  obstructionSide?: readonly import("@nav-games/nav-lights").DayShape[];
  safeSide?: readonly import("@nav-games/nav-lights").DayShape[];
  vessels: string[];
  rule: string;
}

function groupByDayShapes(): DayShapeGroup[] {
  const map = new Map<string, DayShapeGroup>();
  for (const id of VESSEL_IDS) {
    const v = NAV_LIGHTS[id]!;
    if (v.dayShapes.length === 0) continue;
    // Include side hoists in the signature so the dredger's
    // ball-diamond-ball + side markers doesn't collapse onto the
    // plain RAM group.
    const sig = [
      v.dayShapes.join("|"),
      v.dayShapesObstructionSide?.join("|") ?? "",
      v.dayShapesSafeSide?.join("|") ?? "",
    ].join("::");
    const existing = map.get(sig);
    if (existing) {
      existing.vessels.push(v.name);
    } else {
      map.set(sig, {
        signature: sig,
        shapes: v.dayShapes,
        ...(v.dayShapesObstructionSide
          ? { obstructionSide: v.dayShapesObstructionSide }
          : {}),
        ...(v.dayShapesSafeSide ? { safeSide: v.dayShapesSafeSide } : {}),
        vessels: [v.name],
        rule: v.dayShapeRule,
      });
    }
  }
  return [...map.values()];
}

export function NatoSection() {
  return (
    <>
      <div className="card">
        <h3>Letters</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {Object.values(NATO_LETTERS).map((e) => (
              <tr key={e.char}>
                <td style={{ fontWeight: 600, width: 32 }}>{e.char}</td>
                <td style={{ fontWeight: 600 }}>{e.word}</td>
                <td className="muted">{e.pronunciation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h3>Digits</h3>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {Object.values(NATO_DIGITS).map((e) => (
              <tr key={e.char}>
                <td style={{ fontWeight: 600, width: 32 }}>{e.char}</td>
                <td style={{ fontWeight: 600 }}>{e.word}</td>
                <td className="muted">{e.pronunciation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function ChartworkSection() {
  return (
    <>
      <ProjectionsPanel />
      <DatumsPanel />
      <ChartSymbolsPanel />
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Reading the diagrams</h3>
        <p className="small muted" style={{ margin: "0 0 8px" }}>
          The diagrams in this section use a colour key so you can pick
          out each kind of line at a glance:
        </p>
        <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
          <li>
            <span style={{ color: "#d97706", fontWeight: 600 }}>Orange</span>{" "}
            — water track / Dead Reckoning
          </li>
          <li>
            <span style={{ color: "#b8307a", fontWeight: 600 }}>Magenta</span>{" "}
            — Estimated Position / ground track
          </li>
          <li>
            <span style={{ color: "#1e6091", fontWeight: 600 }}>Blue</span>{" "}
            — tidal stream
          </li>
          <li>
            <span style={{ color: "#2c7a3b", fontWeight: 600 }}>Green</span>{" "}
            — Course To Steer plotted in advance
          </li>
        </ul>
        <p className="small muted" style={{ margin: "8px 0 0" }}>
          On the chart in front of you, a sharp pencil is fine — what
          identifies the marks is the <em>symbol</em> (the half-arrow
          on a water track, the triangle on an EP, the three chevrons
          on the tide), not the colour.
        </p>
      </div>

      <CtsCalculator />
      {CHARTWORK_TECHNIQUE_IDS.map((id) => {
        const t = CHARTWORK_TECHNIQUES[id];
        return (
          <div key={id} className="card">
            <h3 style={{ marginTop: 0 }}>
              {t.name}{" "}
              <span className="small muted" style={{ fontWeight: 400 }}>
                ({t.abbreviation})
              </span>
            </h3>
            <p className="small muted" style={{ margin: "0 0 12px" }}>
              {t.summary}
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ChartworkTechniqueFigure technique={t} size={380} />
            </div>
            <h4 style={{ margin: "12px 0 4px" }}>Step by step</h4>
            <ol style={{ marginTop: 0, paddingLeft: 22 }}>
              {t.steps.map((s, i) => (
                <li key={i} className="small" style={{ marginBottom: 4 }}>
                  {s}
                </li>
              ))}
            </ol>
            <p className="small muted" style={{ margin: "8px 0 0" }}>
              <strong>When to use:</strong> {t.whenToUse}
            </p>
          </div>
        );
      })}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Symbols on the chart</h3>
        <p className="small muted" style={{ margin: "0 0 8px" }}>
          The marks the techniques above leave behind, plus the
          chevron annotations on track lines.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: 16,
            marginTop: 8,
            alignItems: "start",
          }}
        >
          {CHARTWORK_IDS.map((id) => {
            const s = CHARTWORK_SYMBOLS[id]!;
            return (
              <div
                key={id}
                style={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    height: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChartworkSymbol symbol={s} size={72} />
                </div>
                <div className="small" style={{ marginTop: 4, fontWeight: 600 }}>
                  {s.name}
                </div>
                {s.abbreviation && (
                  <div className="small muted" style={{ fontSize: 11 }}>
                    {s.abbreviation}
                  </div>
                )}
                <div className="small muted">{s.meaning}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function AstroSection() {
  // Live readouts — useful when a student is at the chart table and
  // just wants to see what the Sun (and Moon) are doing. Recompute on
  // each render; sub-arc-minute changes per second aren't worth a
  // state-driven clock here.
  const now = new Date();
  const sun = sunPosition(now);
  const moon = moonPosition(now);
  const planets = NAV_PLANETS.map((p) => ({ name: p, ...planetPosition(p, now) }));

  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Three coordinates, two grids</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          Astronavigation works in two coordinate systems at once.
          The <strong>equatorial</strong> grid is fixed to the stars:
          a body's <strong>declination</strong> (north/south of the
          celestial equator) and <strong>hour angle</strong> (east/west of
          the Greenwich meridian, in the sky). The <strong>horizon</strong>{" "}
          grid is what you actually see from the boat: altitude (above
          the horizon) and azimuth (clockwise from north).
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          A sight takes a body's altitude with a sextant; sight reduction
          converts that into a position line. The link between the two
          grids is the spherical-triangle formula — covered in Phase 2.
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>The sextant</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          Six parts to know — the <strong>index arm</strong> sweeps the
          arc; the <strong>micrometer drum</strong> reads minutes and
          tenths; the <strong>index mirror</strong> sits on the index
          arm and bounces the body down; the <strong>horizon mirror</strong>{" "}
          (half-silvered) lets you see the horizon and the body together;
          the <strong>shades</strong> protect your eye from the Sun; the{" "}
          <strong>telescope</strong> gives the magnification.
        </p>
        <p className="small muted" style={{ margin: "0 0 6px" }}>
          Reading the scale: the index-arm pointer reads whole degrees
          off the arc; the micrometer drum reads the minutes and tenths.
          A reading of <code>35° 47.3′</code> looks like this:
        </p>
        <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
          <SextantFigure degrees={35} minutes={47.3} showReading />
        </div>
        <p className="small muted" style={{ margin: 0 }}>
          Four errors to check (in this order):{" "}
          <strong>perpendicularity</strong> (index mirror square to the
          frame), <strong>side error</strong> (horizon mirror likewise),{" "}
          <strong>index error</strong> (any residual zero offset), and{" "}
          <strong>collimation</strong> (telescope axis parallel to the
          frame). The first three are adjustable with the mirror screws.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginTop: 12,
          }}
        >
          {(
            [
              "perpendicularity",
              "side",
              "index",
              "collimation",
            ] as readonly SextantErrorKind[]
          ).map((k) => {
            const ex = sextantErrorExplain(k);
            return (
              <div
                key={k}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: 8,
                  background: "var(--bg-elev)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <SextantErrorFigure kind={k} size={180} />
                </div>
                <p
                  className="small"
                  style={{ margin: "6px 0 2px", fontWeight: 600 }}
                >
                  {sextantErrorLabel(k)}
                </p>
                <p className="small muted" style={{ margin: 0 }}>
                  {ex.fix}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>From Hs to Ho — the correction chain</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          The raw sextant reading <code>Hs</code> isn't the angle you
          plot from — it has to be corrected in a fixed order:
        </p>
        <ol className="small" style={{ paddingLeft: 18, marginTop: 0 }}>
          <li>
            <strong>Index error</strong> — instrument zero offset; "on
            the arc" subtracts, "off the arc" adds.
          </li>
          <li>
            <strong>Dip</strong> — your eye is above the sea, so the
            visible horizon dips below true horizontal by{" "}
            ≈ 1.76·√height(m) minutes. Always subtractive.
          </li>
          <li>
            <strong>Refraction</strong> — atmosphere bends light, lifting
            low bodies. Always subtractive; biggest near the horizon.
          </li>
          <li>
            <strong>Semi-diameter</strong> — for the Sun (and Moon) the
            sextant brought a <em>limb</em> down, not the centre. Add
            SD for a lower-limb sight, subtract for an upper-limb one.
          </li>
          <li>
            <strong>Parallax</strong> — small correction for the
            observer being off Earth's centre. Add it.
          </li>
        </ol>
      </div>

      <SextantCorrectionCalculator />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Sight reduction — the intercept method</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          Take an Assumed Position (your DR rounded to a whole degree of
          latitude and the nearest whole-degree LHA). Look up the body's
          GHA and Dec in the Almanac for the moment of the sight. The
          spherical-triangle formula gives the altitude you'd expect
          (Hc) and the body's bearing (Zn). The difference Ho − Hc, in
          arc-minutes, is the intercept in nautical miles: positive →{" "}
          <strong>Towards</strong> the body (HoMoTo); negative →{" "}
          <strong>Away</strong>. Plot it along the azimuth line from the
          AP and that's your position line.
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          GHA &amp; Dec — the &quot;yellow pages&quot; workflow
        </h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 6 }}>
          The Nautical Almanac prints the body's GHA and Dec for every
          whole hour UT, plus two correction columns:{" "}
          <code>v</code> for the body's deviation from the nominal
          GHA rate, and <code>d</code> for the Dec change rate. To
          land at the actual sight time you add three small
          arithmetic corrections — the&nbsp;<em>increment</em> for the
          minutes/seconds at the nominal rate, the&nbsp;
          <em>v-correction</em> for the deviation, and the&nbsp;
          <em>d-correction</em> for the declination.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          Two methods give the same answer to one or two arc-seconds:
        </p>
        <ul className="small muted" style={{ marginTop: 4 }}>
          <li>
            <strong>RYA / Almanac method (taught):</strong> increment at
            a nominal rate (15°/hr Sun &amp; planets · 15.04107°/hr
            Aries · 14.31667°/hr Moon) plus v / d corrections from the
            daily-page columns.
          </li>
          <li>
            <strong>Standard / continuous method:</strong> compute GHA
            and Dec directly from the body's ephemeris at the exact
            sight instant. More accurate by ~1″, but you need a
            computer in your pocket — which is what the
            calculator below does.
          </li>
        </ul>
      </div>

      <AlmanacIncrementCalculator />

      <SightReductionCalculator />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          Two methods for the sight: modern vs. RYA tables
        </h3>
        <p className="small muted" style={{ margin: "0 0 6px" }}>
          The Hc + Zn we just computed has two equivalent paths through
          the spherical triangle:
        </p>
        <ul className="small muted" style={{ marginTop: 0 }}>
          <li>
            <strong>Modern / standard:</strong> work the formula
            directly — <code>sin Hc = sin L · sin D + cos L · cos D · cos LHA</code>{" "}
            and <code>cos Z = (sin D − sin Hc · sin L) / (cos Hc · cos L)</code>.
            Two trig calls per sight. The calculator above defaults to
            this method.
          </li>
          <li>
            <strong>RYA tables (haversine + ABC):</strong> finish the
            arithmetic with Norie's tables. Haversine gives Hc with
            sign-free additions; ABC names a Z in two table lookups,
            then a quadrant rule gives Zn. Tick&nbsp;
            <em>&quot;Show the RYA tables method&quot;</em> in the
            calculator to see this working alongside.
          </li>
        </ul>
        <p className="small muted" style={{ margin: 0 }}>
          The methods are algebraically identical — pick whichever is
          easier in the conditions you're working in.
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Pro forma — the sight worksheet</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 6 }}>
          The RYA exam expects every sight on a worksheet — Hs → Ho,
          almanac extract, increments, LHA, haversine Hc, ABC Zn,
          intercept. The pro forma in the tools area prints a blank A4
          copy for hand-working, or auto-populates every cell from
          your numbers so you can check your hand-working line by
          line.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          Find it under <em>Games &amp; Tools → Astronavigation →
          Tools → Sight reduction pro forma</em>.
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Meridian altitude — the noon-Sun shortcut</h3>
        <p className="small muted" style={{ margin: 0 }}>
          When a body sits on your meridian (the instant the altitude
          stops rising), the spherical triangle collapses and latitude
          falls straight out without sight reduction:
          {" "}<code>Lat = Dec ± (90° − Ho)</code> — sign set by which
          side of you the body bore at transit.
        </p>
      </div>

      <MeridianAltitudeCalculator />

      <TwilightCalculator />

      <MoonPhasePanel />

      <PlanetVisibilityPanel />

      <StarChart />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>The 58 navigational stars</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          The set the Nautical Almanac tabulates — sorted alphabetically
          here, with SHA, declination, magnitude (lower = brighter) and
          modern constellation. Values are mid-decade epoch; for real
          plotting use the current Almanac. Stars with declination
          higher than your latitude pass through your zenith — handy
          when picking a meridian-transit target.
        </p>
        <div style={{ maxHeight: 360, overflow: "auto", border: "1px solid var(--border)", borderRadius: 6 }}>
          <table style={{ width: "100%", fontSize: 13 }}>
            <thead style={{ position: "sticky", top: 0, background: "var(--bg-card)" }}>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 8px" }}>Star</th>
                <th style={{ textAlign: "right", padding: "6px 8px" }}>SHA</th>
                <th style={{ textAlign: "right", padding: "6px 8px" }}>Dec</th>
                <th style={{ textAlign: "right", padding: "6px 8px" }}>Mag</th>
                <th style={{ textAlign: "left", padding: "6px 8px" }}>In</th>
              </tr>
            </thead>
            <tbody>
              {NAV_STARS.map((s) => (
                <tr key={s.name}>
                  <td style={{ padding: "4px 8px", fontWeight: 600 }}>{s.name}</td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    {formatDegMin(s.shaDeg)}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }} className="muted">
                    {formatDeclination(s.decDeg)}
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }} className="muted">
                    {s.magnitude.toFixed(1)}
                  </td>
                  <td style={{ padding: "4px 8px" }} className="muted">
                    {s.constellation}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>The Sun, right now</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          Computed from your device clock — handy when there's no Almanac
          to hand. Accurate to about a minute of arc, plenty for practice
          sights.
        </p>
        <table style={{ width: "100%", fontSize: 14, maxWidth: 360 }}>
          <tbody>
            <tr>
              <td style={{ padding: "4px 8px", fontWeight: 600 }}>Declination</td>
              <td className="muted" style={{ padding: "4px 8px" }}>
                {formatDeclination(sun.decDeg)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "4px 8px", fontWeight: 600 }}>GHA</td>
              <td className="muted" style={{ padding: "4px 8px" }}>
                {formatDegMin(sun.ghaDeg)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "4px 8px", fontWeight: 600 }}>Equation of Time</td>
              <td className="muted" style={{ padding: "4px 8px" }}>
                {sun.eotMinutes >= 0 ? "+" : "−"}
                {Math.abs(sun.eotMinutes).toFixed(1)} min
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>The Moon, right now</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          Low-precision ephemeris (~10–20′ position accuracy). Note the
          horizontal parallax — at ~57′ it's the dominant correction
          on a Moon sight, dwarfing dip and refraction combined.
        </p>
        <table style={{ width: "100%", fontSize: 14, maxWidth: 380 }}>
          <tbody>
            <tr>
              <td style={{ padding: "4px 8px", fontWeight: 600 }}>Declination</td>
              <td className="muted" style={{ padding: "4px 8px" }}>
                {formatDeclination(moon.decDeg)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "4px 8px", fontWeight: 600 }}>GHA</td>
              <td className="muted" style={{ padding: "4px 8px" }}>
                {formatDegMin(moon.ghaDeg)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "4px 8px", fontWeight: 600 }}>Distance</td>
              <td className="muted" style={{ padding: "4px 8px" }}>
                {Math.round(moon.distanceKm).toLocaleString()} km
              </td>
            </tr>
            <tr>
              <td style={{ padding: "4px 8px", fontWeight: 600 }}>Horizontal parallax</td>
              <td className="muted" style={{ padding: "4px 8px" }}>
                {moon.hpArcmin.toFixed(1)}′
              </td>
            </tr>
            <tr>
              <td style={{ padding: "4px 8px", fontWeight: 600 }}>Semi-diameter</td>
              <td className="muted" style={{ padding: "4px 8px" }}>
                {moon.sdArcmin.toFixed(1)}′
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>The planets, right now</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          Computed from Keplerian orbital elements (~1–3′ position
          accuracy). Magnitude lower than ~3.5 means you can probably
          find it at nautical twilight. Planet sextant sights skip SD
          and parallax — a Sun-style correction chain truncated at
          refraction.
        </p>
        <table style={{ width: "100%", fontSize: 14 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "4px 8px" }}>Planet</th>
              <th style={{ textAlign: "right", padding: "4px 8px" }}>SHA-ish (GHA)</th>
              <th style={{ textAlign: "right", padding: "4px 8px" }}>Dec</th>
              <th style={{ textAlign: "right", padding: "4px 8px" }}>Distance</th>
              <th style={{ textAlign: "right", padding: "4px 8px" }}>Mag</th>
            </tr>
          </thead>
          <tbody>
            {planets.map((p) => (
              <tr key={p.name}>
                <td style={{ padding: "4px 8px", fontWeight: 600, textTransform: "capitalize" }}>
                  {p.name}
                </td>
                <td className="muted" style={{ padding: "4px 8px", textAlign: "right" }}>
                  {formatDegMin(p.ghaDeg)}
                </td>
                <td className="muted" style={{ padding: "4px 8px", textAlign: "right" }}>
                  {formatDeclination(p.decDeg)}
                </td>
                <td className="muted" style={{ padding: "4px 8px", textAlign: "right" }}>
                  {p.distanceAu.toFixed(2)} AU
                </td>
                <td className="muted" style={{ padding: "4px 8px", textAlign: "right" }}>
                  {p.magnitude.toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function KnotsSection() {
  const byCat = Object.values(KNOTS).reduce<Record<string, Knot[]>>(
    (acc, k) => {
      (acc[k.category] ??= []).push(k);
      return acc;
    },
    {},
  );
  return (
    <>
      {KNOT_CATEGORIES.map((c) => (
        <div key={c} className="card">
          <h3>{knotCategoryLabel(c)}</h3>
          <ul style={{ paddingLeft: 18, marginTop: 8 }}>
            {(byCat[c] ?? []).map((k) => (
              <li key={k.id} style={{ marginBottom: 6 }}>
                <strong>{k.name}</strong>{" "}
                <span className="muted">— {k.use}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

export function DistressSection() {
  const mayday = findMnemonic("mayday-call");
  const byCat = Object.values(DISTRESS_SIGNALS).reduce<
    Record<string, DistressSignal[]>
  >((acc, s) => {
    (acc[s.category] ??= []).push(s);
    return acc;
  }, {});
  return (
    <>
      {mayday && <MnemonicCard m={mayday} />}
      <div
        className="card"
        style={{
          borderLeft: "4px solid #c0382b",
          background: "rgba(192, 56, 43, 0.05)",
        }}
      >
        <h3 style={{ marginTop: 0, color: "#c0382b" }}>
          Mayday vs Pan-Pan — pick the right call
        </h3>
        <p className="small" style={{ margin: "0 0 6px" }}>
          <strong style={{ color: "#c0382b" }}>Mayday</strong> —
          DISTRESS. Grave and imminent danger to vessel or person.
          Press DSC; voice call on Ch 16 requires IMMEDIATE assistance.
        </p>
        <p className="small" style={{ margin: 0 }}>
          <strong style={{ color: "#b45309" }}>Pan-Pan</strong> —
          URGENCY. Very urgent safety message but NOT immediately
          life-threatening. Voice call on Ch 16 to "all stations";
          no DSC distress.
        </p>
      </div>
      {DISTRESS_CATEGORIES.map((c) => (
        <div key={c} className="card">
          <h3>{distressCategoryLabel(c)}</h3>
          <table style={{ width: "100%", fontSize: 14 }}>
            <tbody>
              {(byCat[c] ?? []).map((s) => {
                const colour =
                  s.priority === "urgency"
                    ? "#b45309"
                    : s.priority === "safety"
                      ? "#0c4a6e"
                      : undefined;
                return (
                  <tr key={s.id}>
                    <td
                      style={{
                        fontWeight: 600,
                        paddingRight: 12,
                        verticalAlign: "top",
                        width: "35%",
                        color: colour,
                      }}
                    >
                      {s.name}
                    </td>
                    <td className="muted">{s.shortMeaning}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

export function WeatherSection() {
  const buys = findMnemonic("buys-ballot");
  const byCat = Object.values(WEATHER_TERMS).reduce<Record<string, WeatherTerm[]>>(
    (acc, t) => {
      (acc[t.category] ??= []).push(t);
      return acc;
    },
    {},
  );
  return (
    <>
      <SynopticPanel />
      {buys && <MnemonicCard m={buys} />}
      <BuysBallotPanel />
      <PressureFeaturesPanel />
      <FrontsPanel />
      <DepressionLifecyclePanel />
      <CoastalBreezesPanel />
      <FogTypesPanel />
      <CloudsPanel />
      {WEATHER_CATEGORIES.map((c) => (
        <div key={c} className="card">
          <h3>{categoryLabel(c)}</h3>
          <table style={{ width: "100%", fontSize: 14 }}>
            <tbody>
              {(byCat[c] ?? []).map((t) => (
                <tr key={t.id}>
                  <td
                    style={{
                      fontWeight: 600,
                      paddingRight: 12,
                      verticalAlign: "top",
                      width: "40%",
                    }}
                  >
                    {t.term}
                  </td>
                  <td className="muted">{t.shortMeaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Original Beaufort scale — the visual-observation table the
          shipping-forecast categories are derived from. Distinct
          from the Met Office "Light / Moderate / Fresh / ..." set
          above because Beaufort goes finer (13 levels) and includes
          the sea-state criteria each force was designed around. */}
      <div className="card">
        <h3>Beaufort scale (sea criteria)</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Sir Francis Beaufort's 1805 scale — designed to estimate
          wind force by eye from the sea surface. Each force pairs a
          wind speed with the visible effects on the water (and on
          land, for completeness). Modern Met Office wind descriptors
          collapse these into broader categories — "Light" covers
          F1-3, "Fresh" is F5, "Strong" is F6, etc.
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={{ padding: "6px 8px 6px 0", width: "8%" }}>F</th>
                <th style={{ padding: "6px 8px", width: "18%" }}>Name</th>
                <th style={{ padding: "6px 8px", width: "14%" }}>Knots</th>
                <th style={{ padding: "6px 8px", width: "12%" }}>
                  Probable wave ht.
                </th>
                <th style={{ padding: "6px 8px" }}>Sea criteria</th>
              </tr>
            </thead>
            <tbody>
              {BEAUFORT_SCALE.map((b) => {
                const knotsLabel =
                  b.knots.high === Number.POSITIVE_INFINITY
                    ? `≥ ${b.knots.low}`
                    : b.knots.low === b.knots.high
                      ? `${b.knots.low}`
                      : `${b.knots.low}–${b.knots.high}`;
                return (
                  <tr
                    key={b.force}
                    style={{
                      verticalAlign: "top",
                      borderTop: "1px solid var(--border)",
                    }}
                  >
                    <td
                      style={{
                        padding: "8px 8px 8px 0",
                        fontWeight: 700,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                      }}
                    >
                      {b.force}
                    </td>
                    <td style={{ padding: "8px", fontWeight: 600 }}>
                      {b.name}
                    </td>
                    <td className="muted" style={{ padding: "8px" }}>
                      {knotsLabel}
                    </td>
                    <td className="muted" style={{ padding: "8px" }}>
                      {b.probableWaveHeight}
                    </td>
                    <td className="muted" style={{ padding: "8px" }}>
                      {b.seaCriteria}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="small muted" style={{ marginTop: 8, marginBottom: 0 }}>
          Wave heights are for fully-developed seas in the open
          ocean — sheltered waters typically see lower heights at
          the same force.
        </p>
      </div>
      <IsobarWindPanel />
      <ShippingForecastPanel />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sailing section — parts of a sail + points of sail.
// ────────────────────────────────────────────────────────────────────

export function SailingSection() {
  return (
    <>
      <BoatAnatomyPanel />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Parts of a sail</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          A Bermudan mainsail labelled with the names every sailing
          student needs. Headsails (jibs, genoas) share the corners
          and edges but skip the batten / reef / boom hardware.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 8,
            marginBottom: 12,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <SailPartsFigure showAllLabels size={300} />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 10,
          }}
        >
          {SAIL_PARTS.map((p) => (
            <div
              key={p.id}
              style={{
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: 8,
                background: "var(--bg-elev)",
              }}
            >
              <div className="small" style={{ fontWeight: 700 }}>
                {p.name}
              </div>
              <p className="small muted" style={{ margin: "4px 0 0" }}>
                {p.meaning}
              </p>
              <p
                className="small"
                style={{
                  margin: "6px 0 0",
                  fontStyle: "italic",
                  color: "var(--text-muted)",
                }}
              >
                {p.use}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Points of sail</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          Boat headings relative to the true wind. The wind blows
          downward in the figure; a boat&apos;s heading is named for
          the angle between its bow and the wind.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 8,
            marginBottom: 12,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <PointsOfSailFigure showAllLabels size={320} />
        </div>
        <table
          className="small"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr style={{ textAlign: "left", color: "var(--text-muted)" }}>
              <th style={{ padding: "4px 6px" }}>Point</th>
              <th style={{ padding: "4px 6px" }}>~Angle</th>
              <th style={{ padding: "4px 6px" }}>Sail trim</th>
            </tr>
          </thead>
          <tbody>
            {POINTS_OF_SAIL.map((p) => (
              <tr key={p.id}>
                <td style={{ padding: "6px 6px", fontWeight: 600 }}>
                  {p.name}
                </td>
                <td style={{ padding: "6px 6px" }} className="muted">
                  {p.midAngleDeg}° ± {p.halfWidthDeg}°
                </td>
                <td style={{ padding: "6px 6px" }} className="muted">
                  {p.trim}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="small muted" style={{ margin: "8px 0 0" }}>
          Tacks and gybes are <em>transitions</em>, not points of sail:
          a tack swings the bow through the wind (head-to-wind, sails
          flap, settle on the opposite close-hauled); a gybe swings
          the stern through the wind (boom crosses the boat — rig a
          preventer or play the mainsheet across to control it).
        </p>
      </div>

      {(() => {
        const fe = findMnemonic("five-essentials");
        return fe ? <MnemonicCard m={fe} /> : null;
      })()}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Sail-handling sequences</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
          The five core procedures every RYA sailing student is
          examined on, with the call-and-response. Drill them with the
          &quot;Sail-handling sequences&quot; game in the Sailing
          module.
        </p>
        {SAIL_MANOEUVRES.map((m) => (
          <SailManoeuvreCard key={m.id} m={m} />
        ))}
      </div>
      <AdvancedSailingPanel />
      <LaunchProceduresPanel />
    </>
  );
}

function SailManoeuvreCard({ m }: { m: (typeof SAIL_MANOEUVRES)[number] }) {
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        padding: 12,
        marginBottom: 12,
        background: "var(--bg-elev)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 4,
        }}
      >
        <strong style={{ fontSize: 16 }}>{m.name}</strong>
        <span className="small muted">({m.course})</span>
      </div>
      <p className="small" style={{ margin: "4px 0 0" }}>
        {m.summary}
      </p>
      <p
        className="small muted"
        style={{ margin: "6px 0 0", fontStyle: "italic" }}
      >
        <strong>When:</strong> {m.whenToUse}
      </p>

      <ol style={{ margin: "10px 0 0", paddingLeft: 18 }}>
        {m.steps.map((s, i) => (
          <ManoeuvreStepLi key={i} s={s} />
        ))}
      </ol>

      <p className="small muted" style={{ margin: "10px 0 4px", fontWeight: 600 }}>
        Common mistakes:
      </p>
      <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
        {m.pitfalls.map((p, i) => (
          <li key={i} style={{ marginBottom: 2 }}>
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

const ANATOMY_VIEWS: ReadonlyArray<{
  id: BoatView;
  label: string;
  blurb: string;
}> = [
  {
    id: "yacht-profile",
    label: "Yacht profile",
    blurb:
      "Side view — hull, deck, cockpit, cabin areas, plus the deck gear every Competent Crew student knows by name.",
  },
  {
    id: "rigging",
    label: "Standing & running rigging",
    blurb:
      "Standing rigging (mast, boom, stays, spreaders) keeps the rig up; running rigging (halyards, sheets, kicker, outhaul) controls the sails.",
  },
  {
    id: "powerboat",
    label: "Powerboat profile",
    blurb:
      "Planing hull, console, kill-cord, outboard / sterndrive, trim tabs — the vocabulary for RYA Powerboat L1 + L2.",
  },
  {
    id: "below-decks",
    label: "Below decks",
    blurb:
      "Engine bay, fuel + water tanks, batteries, raw-water sea-cock + strainer, bilge pump, gas locker.",
  },
];

function BoatAnatomyPanel() {
  const [view, setView] = useState<BoatView>("yacht-profile");
  const parts = partsForView(view);
  const active = ANATOMY_VIEWS.find((v) => v.id === view)!;

  // Group rigging parts by standing / running so the rigging view's
  // legend reads as the student would think of it.
  const grouped =
    view === "rigging"
      ? {
          Standing: parts.filter((p) => p.riggingKind === "standing"),
          Running: parts.filter((p) => p.riggingKind === "running"),
        }
      : null;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Boat anatomy — name the parts</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The vocabulary every RYA student needs by name. Four views,
        tap to switch.
      </p>

      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        {ANATOMY_VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={view === v.id ? "" : "secondary"}
            onClick={() => setView(v.id)}
            style={{ flex: "1 1 120px", fontSize: 13, padding: "8px 6px" }}
            aria-pressed={view === v.id}
          >
            {v.label}
          </button>
        ))}
      </div>

      <p className="small muted" style={{ margin: "0 0 10px" }}>
        {active.blurb}
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: 8,
          marginBottom: 12,
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
        }}
      >
        <BoatPartsFigure view={view} showAllLabels size={360} />
      </div>

      {grouped ? (
        <>
          {(["Standing", "Running"] as const).map((g) => (
            <div key={g} style={{ marginBottom: 12 }}>
              <h4
                style={{
                  margin: "8px 0 6px",
                  fontSize: 14,
                  color: "var(--text)",
                }}
              >
                {g} rigging
              </h4>
              <PartCardGrid parts={grouped[g]} />
            </div>
          ))}
        </>
      ) : (
        <PartCardGrid parts={parts} />
      )}
    </div>
  );
}

function PartCardGrid({ parts }: { parts: readonly BoatPart[] }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 10,
      }}
    >
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 8,
            background: "var(--bg-elev)",
          }}
        >
          <div className="small" style={{ fontWeight: 700 }}>
            {p.name}
          </div>
          <p className="small muted" style={{ margin: "4px 0 0" }}>
            {p.meaning}
          </p>
          <p
            className="small"
            style={{
              margin: "6px 0 0",
              fontStyle: "italic",
              color: "var(--text-muted)",
            }}
          >
            {p.use}
          </p>
        </div>
      ))}
    </div>
  );
}

function ManoeuvreStepLi({ s }: { s: ManoeuvreStep }) {
  const colour =
    s.actor === "helm" ? "#1e6091" : s.actor === "crew" ? "#c08428" : "#555";
  const label = s.actor === "helm" ? "Helm" : s.actor === "crew" ? "Crew" : "Both";
  return (
    <li className="small" style={{ marginBottom: 6 }}>
      <span
        style={{
          background: colour,
          color: "white",
          borderRadius: 3,
          padding: "1px 6px",
          fontSize: 11,
          fontWeight: 600,
          marginRight: 6,
        }}
      >
        {label}
      </span>
      {s.callout && (
        <span style={{ fontStyle: "italic" }}>
          &ldquo;{s.callout}&rdquo; —{" "}
        </span>
      )}
      {s.action}
      {s.reason && (
        <span className="muted" style={{ display: "block", marginTop: 2 }}>
          ↳ {s.reason}
        </span>
      )}
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────
// MOB recovery — the manoeuvres grouped by vessel, with RYA-method
// callouts under sail and under power.
// ────────────────────────────────────────────────────────────────────

export function MobSection() {
  const manoeuvres = Object.values(MANOEUVRES);
  const sail = manoeuvres.filter((m) => m.vessel === "sail");
  const power = manoeuvres.filter((m) => m.vessel === "power");

  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>The first ten seconds</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 6 }}>
          Whichever recovery method you pick, the first response is
          identical. The RYA Day Skipper / Yachtmaster sequence:
        </p>
        <ol className="small" style={{ marginTop: 0, paddingLeft: 18 }}>
          <li>
            <strong>Shout</strong> &quot;MAN OVERBOARD!&quot; — every
            crew member knows.
          </li>
          <li>
            <strong>Point</strong> — a designated lookout points
            constantly at the casualty and does <em>nothing else</em>.
          </li>
          <li>
            <strong>Throw</strong> — lifering / dan-buoy / MOB module
            into the water immediately to mark the position and give
            the casualty something to swim to.
          </li>
          <li>
            <strong>Press MOB</strong> on the chartplotter / GPS to
            mark a waypoint.
          </li>
          <li>
            <strong>VHF</strong> — call PAN PAN (or MAYDAY for life-
            threatening) on Channel 16 with position.
          </li>
        </ol>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Recovery under SAIL</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
          RYA-taught sail-boat methods. <strong>Figure-of-eight</strong>{" "}
          is the standard textbook approach for crewed yachts;{" "}
          <strong>quick stop</strong> is the racing-fleet alternative
          that keeps the boat close to the casualty at the cost of a
          more demanding manoeuvre.
        </p>
        {sail.map((m) => (
          <ManoeuvreCard key={m.id} m={m} />
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Recovery under POWER</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
          For motorboats and yachts under engine. The{" "}
          <strong>Williamson turn</strong> is the classic SOLAS method
          (returns you onto the reciprocal track — useful in fog or at
          night when you&apos;ve lost sight). <strong>Anderson</strong>{" "}
          is the fastest. <strong>Scharnow</strong> sits between them
          and is preferred by some large-vessel watchkeepers.
        </p>
        {power.map((m) => (
          <ManoeuvreCard key={m.id} m={m} />
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Which method, when?</h3>
        <ul className="small" style={{ marginTop: 0, paddingLeft: 18 }}>
          <li>
            <strong>
              Crewed yacht under sail, casualty still in sight
            </strong>{" "}
            → figure-of-eight (predictable, ends head-to-wind for the
            pick-up).
          </li>
          <li>
            <strong>
              Short-handed sail, casualty in sight, room is limited
            </strong>{" "}
            → quick stop (heave-to immediately, then short legs back).
          </li>
          <li>
            <strong>Under engine in good visibility</strong> → Anderson
            turn (fastest return to the casualty).
          </li>
          <li>
            <strong>
              Lost sight of the casualty / at night / in fog
            </strong>{" "}
            → Williamson turn (puts you back on the reciprocal track,
            highest chance of re-acquiring).
          </li>
        </ul>
      </div>

      <SearchPatternsPanel />
    </>
  );
}

function SearchPatternsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>SAR search patterns (IAMSAR)</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        When the initial recovery has been unsuccessful + the
        casualty is missing, you move from a recovery manoeuvre to a
        search pattern. The IAMSAR Volume III patterns scale to the
        size of the search area + how well you know the datum.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 12,
        }}
      >
        {SEARCH_PATTERNS.map((p) => (
          <div
            key={p.id}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "baseline",
                marginBottom: 6,
              }}
            >
              <strong>{p.name}</strong>
              <span
                style={{
                  fontSize: 10,
                  background: "var(--accent)",
                  color: "white",
                  padding: "1px 6px",
                  borderRadius: 3,
                  fontWeight: 700,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {p.code}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
              <SearchPatternFigure pattern={p.id} size={220} />
            </div>
            <p className="small" style={{ margin: "4px 0" }}>
              {p.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>When:</strong> {p.whenToUse}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Geometry:</strong> {p.geometry}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Best for:</strong> {p.suitedFor}
            </p>
            <p
              className="small muted"
              style={{ margin: "6px 0 2px", fontWeight: 600 }}
            >
              Pitfalls:
            </p>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {p.pitfalls.map((pf, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {pf}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function ManoeuvreCard({ m }: { m: (typeof MANOEUVRES)[string] }) {
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        padding: 10,
        marginBottom: 10,
        background: "var(--bg-elev)",
      }}
    >
      <div className="small" style={{ fontWeight: 700, marginBottom: 4 }}>
        {m.name}
      </div>
      <p className="small muted" style={{ margin: "0 0 6px" }}>
        {m.summary}
      </p>
      <p
        className="small"
        style={{
          margin: "0 0 6px",
          fontStyle: "italic",
          color: "var(--text-muted)",
        }}
      >
        <strong>Best for:</strong> {m.bestFor}
      </p>
      <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
        {m.steps.map((s, i) => (
          <li key={i} style={{ marginBottom: 2 }}>
            {s}
          </li>
        ))}
      </ol>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Engine — the WOBBLE pre-engine-start mnemonic.
// ────────────────────────────────────────────────────────────────────

export function EngineSection({
  onNavigate,
}: {
  onNavigate?: (to: string) => void;
} = {}) {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Engine maintenance</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          Two layers: the daily WOBBLE check (below) catches the
          common faults before they bite; the nine recovery + service
          procedures (below WOBBLE) handle breakdowns and scheduled
          maintenance.
        </p>
        {onNavigate && (
          <button onClick={() => onNavigate("/play/engine")}>
            Drill engine fault diagnosis →
          </button>
        )}
      </div>

      <EngineWobblePanel />
      <EngineProceduresPanel />
      <EngineFaultsPanel />
      <EngineSystemsPanel />
      <TroubleshootingPanel />
      <OutboardPanel />
    </>
  );
}

function EngineWobblePanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>WOBBLE — pre-engine checks</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        The RYA mnemonic for the daily checks every skipper runs{" "}
        <em>before</em> turning the key. Two minutes round the engine
        catches almost every common engine-failure root cause.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
        }}
      >
        {WOBBLE_STEPS.map((s) => (
          <div
            key={s.id}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: "var(--accent)",
                  lineHeight: 1,
                }}
              >
                {s.letter}
              </span>
              <span style={{ fontWeight: 700 }}>{s.name}</span>
            </div>
            <p className="small" style={{ margin: "4px 0 6px" }}>
              {s.detail}
            </p>
            <p
              className="small muted"
              style={{ margin: 0, fontStyle: "italic" }}
            >
              <strong>Why:</strong> {s.why}
            </p>
          </div>
        ))}
      </div>

      <p className="small muted" style={{ marginTop: 12, marginBottom: 0 }}>
        A common variation uses <strong>Battery</strong> for the second
        B (terminals clean, charged, isolators on). Either is taught —
        whichever sticks, do it every time.
      </p>

      {(() => {
        const wobble = findMnemonic("wobble");
        return wobble ? (
          <div style={{ marginTop: 12 }}>
            <MnemonicCard m={wobble} compact />
          </div>
        ) : null;
      })()}
    </div>
  );
}

const PROC_CATEGORY_LABEL: Record<string, string> = {
  breakdown: "Breakdown recovery",
  service: "Scheduled service",
  seasonal: "Seasonal (winterise / spring)",
  electrical: "Electrical system",
};

function EngineProceduresPanel() {
  const order: Array<"breakdown" | "service" | "seasonal" | "electrical"> = [
    "breakdown",
    "service",
    "seasonal",
    "electrical",
  ];
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Procedures — beyond WOBBLE</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Nine procedures every owner-skipper should be able to perform.
        Breakdown recovery (diesel bleed · impeller change · strainer
        service) for the moments when something has actually failed;
        scheduled service (belts · oil + filter · fuel filter) for the
        annual care; seasonal (winterise + spring); and electrical
        (battery + alternator).
      </p>
      {order.map((cat) => {
        const procs = ENGINE_PROCEDURES.filter((p) => p.category === cat);
        if (procs.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: 14 }}>
            <h4 style={{ margin: "8px 0 6px", fontSize: 14 }}>
              {PROC_CATEGORY_LABEL[cat]}
            </h4>
            {procs.map((p) => (
              <details
                key={p.id}
                className="card"
                style={{ marginBottom: 8 }}
              >
                <summary
                  style={{
                    cursor: "pointer",
                    listStyle: "none",
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  {p.name}
                  <div
                    className="small muted"
                    style={{ marginTop: 2, fontWeight: 400 }}
                  >
                    {p.summary}
                  </div>
                  <div
                    className="small muted"
                    style={{ marginTop: 4, fontWeight: 400 }}
                  >
                    {p.durationMinutes} · {p.skillLevel}
                  </div>
                </summary>

                <p
                  className="small muted"
                  style={{ margin: "10px 0 6px" }}
                >
                  <strong>When:</strong> {p.whenToDo}
                </p>

                <p
                  className="small muted"
                  style={{ margin: "8px 0 4px", fontWeight: 600 }}
                >
                  Tools:
                </p>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {p.tools.map((t, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {t}
                    </li>
                  ))}
                </ul>

                <p
                  className="small muted"
                  style={{ margin: "8px 0 4px", fontWeight: 600 }}
                >
                  Procedure:
                </p>
                <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {p.steps.map((s, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {s}
                    </li>
                  ))}
                </ol>

                <p
                  className="small muted"
                  style={{ margin: "8px 0 4px", fontWeight: 600 }}
                >
                  Pitfalls:
                </p>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {p.pitfalls.map((pf, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {pf}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function EngineFaultsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Fault → procedure lookup</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Common observable symptoms mapped to the procedure that fixes
        them. Used by the drill game; useful as a quick reference at
        the chart table when something has gone wrong.
      </p>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr
            style={{
              textAlign: "left",
              color: "var(--text-muted)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <th style={{ padding: "6px 8px", width: "55%" }}>Symptom</th>
            <th style={{ padding: "6px 8px" }}>Procedure</th>
          </tr>
        </thead>
        <tbody>
          {ENGINE_FAULTS.map((f) => {
            const proc = ENGINE_PROCEDURES.find((p) => p.id === f.fixedBy);
            return (
              <tr
                key={f.id}
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <td style={{ padding: "8px" }}>
                  {f.symptom}
                  <div
                    className="small muted"
                    style={{ marginTop: 4, fontStyle: "italic" }}
                  >
                    {f.rationale}
                  </div>
                </td>
                <td style={{ padding: "8px", fontWeight: 700 }}>
                  {proc?.name ?? f.fixedBy}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Mnemonics — the unified RYA-mnemonic catalogue + a drill launcher.
// Every entry is also embedded in its native domain section, so a
// student revising e.g. tides sees the Rule of Twelfths in context;
// this section is the single page for cramming them all together.
// ────────────────────────────────────────────────────────────────────

export function MnemonicsSection({
  onNavigate,
}: {
  onNavigate?: (to: string) => void;
}) {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>The RYA mnemonics</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          The handful of mnemonics every RYA student should have at
          their fingertips. Each one also appears in its native module
          (CADET in Compass error, WOBBLE in Engine, Five Essentials
          in Sailing, Rule of Twelfths in Tides, Buys Ballot in Met
          Office weather, the IRPCS pecking order in IRPCS rules,
          MAYDAY in Distress signals) — this page is the all-in-one
          revision view.
        </p>
        {onNavigate && (
          <button onClick={() => onNavigate("/play/mnemonics")}>
            Drill mnemonics →
          </button>
        )}
      </div>

      {MNEMONICS.map((m) => (
        <MnemonicCard key={m.id} m={m} />
      ))}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Synoptic weather panels — fronts, pressure features, depression
// lifecycle, sea/land breezes, fog types, cloud genera. Plus the
// Buys Ballot diagram (a visual companion to the mnemonic card).
// ────────────────────────────────────────────────────────────────────

function SynopticPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Reading a synoptic chart</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The classic UK weather pattern: an Atlantic <strong>depression</strong>{" "}
        (low pressure, L) with attached <strong>warm + cold fronts</strong>{" "}
        approaching from the west, and an <strong>anticyclone</strong>{" "}
        (high pressure, H) sitting to the south-east. Isobars tightly
        packed near the low = strong winds; widely spaced near the high
        = light winds.
      </p>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <SynopticChartFigure size={520} />
      </div>
    </div>
  );
}

function BuysBallotPanel() {
  const [hem, setHem] = useState<"N" | "S">("N");
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Buys Ballot&apos;s Law — visual</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Stand with your back to the wind. The low-pressure centre is to
        your <strong>left</strong> in the northern hemisphere, to your{" "}
        <strong>right</strong> in the southern. Follows from the
        Coriolis force deflecting air to the right (N) / left (S) of
        the pressure-gradient direction.
      </p>
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        {(["N", "S"] as const).map((h) => (
          <button
            key={h}
            type="button"
            className={hem === h ? "" : "secondary"}
            onClick={() => setHem(h)}
            style={{ flex: "1 1 140px" }}
            aria-pressed={hem === h}
          >
            {h === "N" ? "Northern hemisphere" : "Southern hemisphere"}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <BuysBallotFigure hemisphere={hem} size={360} />
      </div>
    </div>
  );
}

function PressureFeaturesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Pressure features</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The five surface-chart shapes a navigator needs to recognise.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 10,
        }}
      >
        {PRESSURE_FEATURES.map((p) => (
          <div
            key={p.id}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "baseline",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "var(--accent)",
                }}
              >
                {p.symbol}
              </span>
              <strong>{p.name}</strong>
            </div>
            <p className="small" style={{ margin: "4px 0" }}>
              {p.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Isobars:</strong> {p.isobarPattern}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Weather:</strong> {p.weather}
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              <strong>Movement:</strong> {p.movement}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FrontsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Fronts — warm, cold, occluded</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        A front is the boundary between two air masses of different
        temperature. The slope of the boundary and the direction of
        motion determine the cloud sequence, pressure trend, wind shift
        and precipitation a navigator sees as it passes overhead.
      </p>
      {FRONTS.map((f) => (
        <div
          key={f.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 12,
            marginBottom: 12,
            background: "var(--bg-elev)",
          }}
        >
          <h4 style={{ margin: "0 0 6px" }}>{f.name}</h4>
          <p className="small muted" style={{ margin: "0 0 6px" }}>
            <strong>Chart symbol:</strong> {f.symbolDescription}
          </p>
          <p className="small" style={{ margin: "0 0 8px" }}>
            {f.description}
          </p>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <FrontCrossSectionFigure kind={f.id} size={420} />
          </div>
          <p className="small" style={{ margin: "6px 0 2px", fontWeight: 600 }}>
            Cloud sequence:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {f.cloudSequence.map((c, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {c}
              </li>
            ))}
          </ol>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 8,
              marginTop: 8,
            }}
          >
            <Box label="Wind" value={f.windShift} />
            <Box label="Pressure" value={f.pressure} />
            <Box label="Precipitation" value={f.precipitation} />
            <Box label="Airmass change" value={f.airMassChange} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Box({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: "var(--bg)",
        border: "1px solid var(--border)",
        borderRadius: 4,
        padding: 6,
      }}
    >
      <div className="small muted" style={{ fontWeight: 600 }}>
        {label}
      </div>
      <div className="small">{value}</div>
    </div>
  );
}

function DepressionLifecyclePanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Depression lifecycle</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        A typical Atlantic depression progresses through six stages in
        2–4 days. Knowing where in the cycle a low sits tells you what
        weather to expect next.
      </p>
      <ol style={{ paddingLeft: 18, margin: 0 }}>
        {DEPRESSION_LIFECYCLE.map((s) => (
          <li key={s.id} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700 }}>
              {s.name}{" "}
              <span className="small muted" style={{ fontWeight: 500 }}>
                ({s.age})
              </span>
            </div>
            <p className="small" style={{ margin: "2px 0" }}>
              {s.summary}
            </p>
            <p className="small muted" style={{ margin: 0 }}>
              <strong>Front structure:</strong> {s.frontStructure}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function CoastalBreezesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Sea + land breezes</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Coastal diurnal circulation — built into the local pattern by
        the differential heating of land vs sea. Both can dominate the
        local wind when the gradient wind is light.
      </p>
      {COASTAL_BREEZES.map((b) => (
        <div
          key={b.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 10,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{b.name}</strong>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Time:</strong> {b.time}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Driver:</strong> {b.driver}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Direction:</strong> {b.direction}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Strength:</strong> {b.strength}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Conditions:</strong> {b.conditions}
          </p>
          <p
            className="small"
            style={{ margin: "6px 0 0", fontStyle: "italic" }}
          >
            <strong>Nav tip:</strong> {b.navTip}
          </p>
        </div>
      ))}
    </div>
  );
}

function FogTypesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Fog types</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Five mechanisms produce marine fog — knowing which type you&apos;re
        in tells you when it will clear.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 10,
        }}
      >
        {FOG_TYPES.map((f) => (
          <div
            key={f.id}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <strong>{f.name}</strong>
            <p className="small" style={{ margin: "4px 0" }}>
              {f.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Formation:</strong> {f.formation}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Conditions:</strong> {f.conditions}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Where / when:</strong> {f.whereWhen}
            </p>
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              <strong>Clearance:</strong> {f.clearance}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CloudsPanel() {
  const order = ["high", "middle", "low", "vertical"] as const;
  const levelLabel: Record<(typeof order)[number], string> = {
    high: "High cloud (5–13 km)",
    middle: "Middle cloud (2–7 km)",
    low: "Low cloud (surface–2 km)",
    vertical: "Vertical development",
  };
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Cloud genera</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The ten WMO cloud genera — what they look like and what weather
        they signal. <em>Mackerel skies and mares&apos; tails / make
        tall ships carry low sails</em> — high cloud is the first sign
        of an approaching warm front.
      </p>
      {order.map((lvl) => (
        <div key={lvl} style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "8px 0 4px", fontSize: 14 }}>
            {levelLabel[lvl]}
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 8,
            }}
          >
            {CLOUDS_BY_LEVEL[lvl].map((c) => (
              <div
                key={c.id}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: 8,
                  background: "var(--bg-elev)",
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  {c.name}{" "}
                  <span className="small muted" style={{ fontWeight: 400 }}>
                    ({c.abbreviation})
                  </span>
                </div>
                <p className="small" style={{ margin: "4px 0" }}>
                  {c.appearance}
                </p>
                <p
                  className="small muted"
                  style={{ margin: 0, fontStyle: "italic" }}
                >
                  {c.weatherSignal}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// VHF + SRC — channels, call types, MMSI structure, DSC distress
// procedure. SRC certificate content + RYA radio-course material.
// ────────────────────────────────────────────────────────────────────

export function CommsSection({
  onNavigate,
}: {
  onNavigate?: (to: string) => void;
}) {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>VHF + SRC essentials</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          The Short Range Certificate (SRC) is the RYA radio
          qualification — required for VHF/DSC operation on UK leisure
          vessels. Three things every SRC candidate must have at
          their fingertips: the dedicated <strong>channels</strong>, the
          four <strong>call types</strong>, and the structure of an{" "}
          <strong>MMSI</strong>.
        </p>
        {onNavigate && (
          <button onClick={() => onNavigate("/play/mayday/vhf")}>
            Drill VHF procedure →
          </button>
        )}
      </div>

      <VhfChannelsPanel />
      <VhfCallTypesPanel />
      <DscDistressPanel />
      <MmsiDecoder />
      <MmsiStructurePanel />
      <RadioLicencingPanel />
      <RtLogPanel />
    </>
  );
}

const CHANNEL_TIER_LABEL: Record<VhfChannel["tier"], string> = {
  distress: "Distress + calling",
  dsc: "DSC",
  "bridge-to-bridge": "Bridge-to-bridge",
  intership: "Inter-ship",
  "port-ops": "Port operations",
  marina: "Marina",
  "coastguard-sar": "Coastguard / SAR",
  weather: "Weather + MSI",
  "private-marine": "Private / commercial",
};

function VhfChannelsPanel() {
  const tierOrder: Array<VhfChannel["tier"]> = [
    "distress",
    "dsc",
    "bridge-to-bridge",
    "coastguard-sar",
    "weather",
    "intership",
    "port-ops",
    "marina",
    "private-marine",
  ];
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>VHF channels — by purpose</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The channel allocations every UK skipper should know. Ch 16 +
        Ch 70 are the two you must never miss; the rest cover the
        common day-to-day situations.
      </p>
      {tierOrder.map((tier) => {
        const inTier = VHF_CHANNELS.filter((c) => c.tier === tier);
        if (inTier.length === 0) return null;
        return (
          <div key={tier} style={{ marginBottom: 12 }}>
            <h4 style={{ margin: "8px 0 4px", fontSize: 14 }}>
              {CHANNEL_TIER_LABEL[tier]}
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 8,
              }}
            >
              {inTier.map((c) => (
                <div
                  key={c.number}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    padding: 8,
                    background: "var(--bg-elev)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: "var(--accent)",
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                      }}
                    >
                      {c.number}
                    </span>
                    <strong style={{ fontSize: 13 }}>{c.purpose}</strong>
                    {c.region === "uk" && (
                      <span
                        style={{
                          fontSize: 10,
                          background: "#1e6091",
                          color: "white",
                          padding: "1px 5px",
                          borderRadius: 3,
                          fontWeight: 600,
                        }}
                      >
                        UK
                      </span>
                    )}
                  </div>
                  <p className="small muted" style={{ margin: "4px 0 0" }}>
                    {c.use}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VhfCallTypesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Call types — Mayday, Pan Pan, Securité, routine</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Four levels of traffic, four procedure words. Mayday for grave
        + imminent danger; Pan Pan for urgent but not life-threatening;
        Securité for safety information (typically broadcast);
        routine for everything else.
      </p>
      {VHF_CALL_TYPES.map((c) => (
        <div
          key={c.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 12,
            marginBottom: 12,
            background: "var(--bg-elev)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 4,
            }}
          >
            <h4 style={{ margin: 0 }}>{c.name}</h4>
            <span className="small muted" style={{ fontStyle: "italic" }}>
              {c.spelling}
            </span>
          </div>
          <p
            className="small"
            style={{
              margin: "4px 0",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontWeight: 700,
              color: "var(--accent)",
            }}
          >
            {c.procedureWord}
          </p>
          <p className="small" style={{ margin: "4px 0 8px" }}>
            {c.summary}
          </p>
          <p className="small muted" style={{ margin: "4px 0 4px", fontWeight: 600 }}>
            Procedure:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {c.steps.map((s, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {s}
              </li>
            ))}
          </ol>
          <p
            className="small"
            style={{
              margin: "8px 0 0",
              padding: 8,
              background: "rgba(44, 122, 59, 0.06)",
              borderLeft: "3px solid #2c7a3b",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
              fontStyle: "italic",
            }}
          >
            <strong>Example:</strong> {c.example}
          </p>
          {c.pitfalls.length > 0 && (
            <>
              <p
                className="small muted"
                style={{ margin: "8px 0 4px", fontWeight: 600 }}
              >
                Common mistakes:
              </p>
              <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                {c.pitfalls.map((p, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>
                    {p}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function DscDistressPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>DSC distress — push, hold, voice</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Digital Selective Calling — the &quot;red button&quot; behind a
        sprung red flap on every modern fixed VHF. Pushed properly, it
        broadcasts your MMSI + position + nature of distress on Ch 70,
        and switches the radio to Ch 16 for voice follow-up.
      </p>
      <ol style={{ marginTop: 0, paddingLeft: 20 }}>
        <li className="small" style={{ marginBottom: 6 }}>
          <strong>Lift the red flap</strong> over the distress button —
          spring-loaded to stop accidental presses.
        </li>
        <li className="small" style={{ marginBottom: 6 }}>
          <strong>If time permits</strong>, select the nature of
          distress from the menu (sinking, fire, MOB, abandoning,
          collision, undesignated). Default = undesignated.
        </li>
        <li className="small" style={{ marginBottom: 6 }}>
          <strong>Press and HOLD the button for 5 seconds</strong>{" "}
          — the radio emits a continuous tone until it fires. A short
          press will NOT send the alert.
        </li>
        <li className="small" style={{ marginBottom: 6 }}>
          The radio transmits the digital alert on{" "}
          <strong>Ch 70</strong> — your MMSI, GPS position, time, and
          nature. Every DSC-equipped receiver in range alerts.
        </li>
        <li className="small" style={{ marginBottom: 6 }}>
          The radio <strong>auto-switches to Ch 16</strong> ready for
          voice follow-up.
        </li>
        <li className="small" style={{ marginBottom: 6 }}>
          <strong>Speak the full voice MAYDAY</strong> on Ch 16. Even
          though the DSC alert went out, voice gives the Coastguard
          the chance to ask questions + reassure you. (See Mayday
          procedure above.)
        </li>
        <li className="small" style={{ marginBottom: 0 }}>
          If you don&apos;t get a response within ~30 seconds, the radio
          will re-transmit the DSC alert automatically until
          acknowledged. Do NOT keep pressing the button — let it work.
        </li>
      </ol>
      <p
        className="small"
        style={{
          marginTop: 10,
          padding: 10,
          background: "rgba(192, 56, 43, 0.10)",
          borderLeft: "3px solid var(--accent)",
          borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
        }}
      >
        <strong>False alarms:</strong> if you accidentally fire a
        distress alert, do NOT just switch off. Switch to Ch 16, call:{" "}
        <em>&quot;All stations all stations all stations · this is
        [vessel name + MMSI] · cancel my distress alert sent at [time]
        · position [position] · my distress alert was sent in error ·
        out&quot;</em>. Then expect the Coastguard to ring you on the
        phone — answer it.
      </p>
    </div>
  );
}

function MmsiStructurePanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>MMSI structure</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Every DSC-equipped station has a unique 9-digit Maritime
        Mobile Service Identity. The leading digits tell you what type
        of station + which country issued it. Common forms:
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
        }}
      >
        {[
          {
            pattern: "MID xxx xxx",
            example: "232 123 456",
            kind: "Ship station",
            note: "Single vessel. Leading three digits = MID (country prefix).",
          },
          {
            pattern: "0 MID x xxxxx",
            example: "0 232 12345",
            kind: "Ship group",
            note: "Group of vessels addressed together — rallies, racing fleets, SAR groups.",
          },
          {
            pattern: "00 MID x xxxx",
            example: "00 232 0014",
            kind: "Coast station",
            note: "Shore-based — UK Coastguard MRCC, port stations.",
          },
          {
            pattern: "111 MID xxx",
            example: "111 232 456",
            kind: "SAR aircraft",
            note: "Search-and-rescue aircraft — helicopters + fixed-wing.",
          },
          {
            pattern: "99 MID xxxx",
            example: "99 232 0123",
            kind: "AIS Aid-to-Navigation",
            note: "AIS-transmitting buoy or beacon (cardinal, lateral, special).",
          },
          {
            pattern: "98 MID xxxx",
            example: "98 232 1234",
            kind: "Auxiliary craft",
            note: "Tender / RIB associated with a parent vessel.",
          },
          {
            pattern: "974 xxxxxx",
            example: "974 123456",
            kind: "AIS EPIRB / PLB",
            note: "Personal locating device, not a vessel.",
          },
        ].map((row) => (
          <div
            key={row.pattern}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <div
              className="small"
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontWeight: 700,
                color: "var(--accent)",
              }}
            >
              {row.pattern}
            </div>
            <div
              className="small muted"
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                marginBottom: 4,
              }}
            >
              e.g. {row.example}
            </div>
            <div style={{ fontWeight: 700 }}>{row.kind}</div>
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              {row.note}
            </p>
          </div>
        ))}
      </div>
      <p className="small muted" style={{ marginTop: 10, marginBottom: 0 }}>
        UK MIDs: <strong>232, 233, 234, 235</strong>. Full MID list is
        in ITU-R M.585 (300+ countries).
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Safety + first aid — flares (with safety procedure), liferaft,
// EPIRB/PLB, lifejackets, first-aid kit categories A/B/C/D, common
// sea casualty scenarios, capsize recovery.
// ────────────────────────────────────────────────────────────────────

export function SafetySection({
  onNavigate,
}: {
  onNavigate?: (to: string) => void;
}) {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Safety + first aid</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          The kit + the casualty knowledge every skipper carries — flares,
          liferaft, EPIRB / PLB, lifejackets, first-aid-kit categories
          A/B/C/D matched to voyage scope, and the six commonest
          casualty scenarios at sea. Plus the four capsize variants
          (dinghy side / inverted / dry + yacht knockdown).
        </p>
        {onNavigate && (
          <button onClick={() => onNavigate("/play/safety")}>
            Drill safety + first aid →
          </button>
        )}
      </div>

      <FlaresPanel />
      <FlarePacksPanel />
      <FlareSafetyPanel />
      <LiferaftPanel />
      <BeaconsPanel />
      <LifejacketsPanel />
      <FirstAidKitsPanel />
      <FirstAidScenariosPanel />
      <CapsizePanel />
      <ReefingBandsPanel />
      <StormTacticsPanel />
      <StormSailsPanel />
      <StabilityPanel />
      <TrsPanel />
    </>
  );
}

function FlaresPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Flare types</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Five recognisable flares. Four are distress signals; one (the
        handheld white) is collision-avoidance only and must NEVER be
        used to alert SAR.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
        }}
      >
        {FLARES.map((f) => (
          <div
            key={f.id}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <strong>{f.name}</strong>
              {!f.isDistress && (
                <span
                  style={{
                    fontSize: 10,
                    background: "var(--accent)",
                    color: "white",
                    padding: "1px 5px",
                    borderRadius: 3,
                    fontWeight: 700,
                  }}
                >
                  NOT distress
                </span>
              )}
            </div>
            <p className="small" style={{ margin: "4px 0" }}>
              {f.summary}
            </p>
            <p
              className="small muted"
              style={{
                margin: "4px 0",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              {f.range}
            </p>
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              <strong>Use:</strong> {f.use}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FlarePacksPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Flare packs by voyage area</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Scale your pack to the voyage. Inshore minimums vs offshore
        SOLAS-style packs.
      </p>
      {FLARE_PACKS.map((p) => (
        <div
          key={p.area}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "baseline",
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <strong style={{ textTransform: "capitalize", fontSize: 15 }}>
              {p.area}
            </strong>
            <span className="small muted">{p.range}</span>
          </div>
          <ul style={{ margin: "4px 0 8px", paddingLeft: 18 }}>
            {p.contents.map((c, i) => {
              const flare = FLARES.find((f) => f.id === c.flare);
              return (
                <li key={i} className="small" style={{ marginBottom: 2 }}>
                  <strong>{c.quantity}</strong> × {flare?.name ?? c.flare}
                </li>
              );
            })}
          </ul>
          <p className="small muted" style={{ margin: 0, fontStyle: "italic" }}>
            {p.note}
          </p>
        </div>
      ))}
    </div>
  );
}

function FlareSafetyPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Firing flares — safety</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Pyrotechnic flares produce sparks and intense heat. Every
        skipper should read these BEFORE the day they need them.
      </p>
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        {FLARE_SAFETY.map((s, i) => (
          <li key={i} className="small" style={{ marginBottom: 4 }}>
            {s}
          </li>
        ))}
      </ol>
    </div>
  );
}

function LiferaftPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Liferaft — choice, launch + grab bag</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Three tiers (coastal · offshore · ocean) by ISO 9650 / SOLAS
        category, with the launch sequence and grab-bag contents every
        skipper rehearses before they need them.
      </p>

      <h4 style={{ margin: "8px 0 4px", fontSize: 14 }}>Liferaft types</h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 10,
          marginBottom: 12,
        }}
      >
        {LIFERAFTS.map((r) => (
          <div
            key={r.type}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <strong style={{ textTransform: "capitalize" }}>{r.type}</strong>
            <p className="small" style={{ margin: "4px 0" }}>
              {r.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Category:</strong> {r.category}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Service:</strong> {r.serviceIntervalYears}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Pack:</strong> {r.equipmentPack}
            </p>
            <p
              className="small"
              style={{ margin: "6px 0 0", fontStyle: "italic" }}
            >
              {r.notes}
            </p>
          </div>
        ))}
      </div>

      <h4 style={{ margin: "12px 0 4px", fontSize: 14 }}>
        Launch + boarding sequence
      </h4>
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        {LIFERAFT_LAUNCH_STEPS.map((s, i) => (
          <li key={i} className="small" style={{ marginBottom: 4 }}>
            {s}
          </li>
        ))}
      </ol>

      <h4 style={{ margin: "12px 0 4px", fontSize: 14 }}>Grab bag contents</h4>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {GRAB_BAG.map((g, i) => (
          <li key={i} className="small" style={{ marginBottom: 2 }}>
            {g}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BeaconsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>EPIRB + PLB</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Two satellite beacons every offshore skipper carries. EPIRB
        registered to the VESSEL; PLB registered to the PERSON. Both
        transmit on 406 MHz to the Cospas-Sarsat constellation.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 10,
        }}
      >
        {BEACONS.map((b) => (
          <div
            key={b.type}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <strong>{b.name}</strong>
            <p className="small" style={{ margin: "4px 0" }}>
              {b.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Activation:</strong> {b.activation}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Registration:</strong> {b.registration}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Battery:</strong> {b.battery}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Transmits:</strong> {b.transmits}
            </p>
            <p
              className="small"
              style={{ margin: "6px 0 0", fontStyle: "italic" }}
            >
              <strong>Best for:</strong> {b.bestFor}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LifejacketsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Lifejacket types</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Four mechanisms: manual, automatic (water-activated bobbin),
        hydrostatic (UML / Hammar — only fires when submerged), and
        inherent (foam). Service intervals + bobbins + cylinders
        matter — an in-date lifejacket that's missed its service is
        not in-date.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 10,
        }}
      >
        {LIFEJACKETS.map((l) => (
          <div
            key={l.type}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <strong>{l.name}</strong>
            <p className="small" style={{ margin: "4px 0" }}>
              {l.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Activation:</strong> {l.activation}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Servicing:</strong> {l.servicing}
            </p>
            <p
              className="small"
              style={{ margin: "6px 0 0", fontStyle: "italic" }}
            >
              <strong>Best for:</strong> {l.bestFor}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FirstAidKitsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>First-aid kit — Cat A / B / C / D</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        MCA distance categories scaled to voyage length. Cat D is the
        coastal day-sail standard; Cat C is the RYA-recommended
        coastal baseline; Cat B for offshore (60–150 nm); Cat A for
        ocean passages (&gt; 150 nm) — Cat A needs a formally-trained
        medic on board.
      </p>

      {FIRST_AID_KITS.map((k) => (
        <details
          key={k.category}
          className="card"
          style={{ marginBottom: 8 }}
        >
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            Cat {k.category} — {k.scope}
            <div
              className="small muted"
              style={{ marginTop: 2, fontWeight: 400 }}
            >
              {k.distance}
            </div>
          </summary>
          <p className="small" style={{ margin: "8px 0" }}>
            {k.summary}
          </p>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Emphasised in this tier:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {k.emphasis.map((e, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {e}
              </li>
            ))}
          </ul>
          <p
            className="small muted"
            style={{ margin: "10px 0 4px", fontWeight: 600 }}
          >
            Contents (representative):
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {k.contents.map((c, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {c}
              </li>
            ))}
          </ul>
        </details>
      ))}

      <div
        style={{
          padding: 12,
          marginTop: 12,
          borderRadius: "var(--radius-sm)",
          background: "rgba(192, 56, 43, 0.06)",
          borderLeft: "3px solid var(--accent)",
        }}
      >
        <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>
          What makes Cat C different from a "standard" first-aid kit?
        </h4>
        <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
          {CAT_C_DIFFERENCES.map((d, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {d}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FirstAidScenariosPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>First aid at sea — the six commonest casualties</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Each scenario shows recognition + immediate action + the
        common mistakes to avoid.
      </p>
      {FIRST_AID_SCENARIOS.map((s) => (
        <div
          key={s.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 12,
            marginBottom: 10,
            background: "var(--bg-elev)",
          }}
        >
          <h4 style={{ margin: "0 0 4px" }}>{s.name}</h4>
          <p className="small" style={{ margin: "4px 0 6px" }}>
            {s.summary}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Recognition:</strong> {s.recognition}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Action:</strong> {s.action}
          </p>
          <p
            className="small"
            style={{
              margin: "6px 0 0",
              padding: 8,
              background: "rgba(192, 56, 43, 0.06)",
              borderLeft: "3px solid var(--accent)",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Do NOT:</strong> {s.pitfalls}
          </p>
        </div>
      ))}
    </div>
  );
}

function CapsizePanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Capsize recovery — dinghy + yacht</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Four capsize variants. The first rule for every one: STAY WITH
        THE BOAT. It floats; you can hold on; rescuers see a boat
        before a head in the water.
      </p>
      {CAPSIZE_SCENARIOS.map((c) => (
        <div
          key={c.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 12,
            marginBottom: 10,
            background: "var(--bg-elev)",
          }}
        >
          <h4 style={{ margin: "0 0 4px" }}>{c.name}</h4>
          <p className="small" style={{ margin: "4px 0 8px" }}>
            {c.summary}
          </p>
          <p
            className="small muted"
            style={{ margin: "4px 0 2px", fontWeight: 600 }}
          >
            Sequence:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 20 }}>
            {c.steps.map((s, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {s}
              </li>
            ))}
          </ol>
          <p
            className="small muted"
            style={{ margin: "8px 0 2px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {c.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Heavy-weather panels — reefing bands, storm tactics, storm sails,
// vessel stability + GZ curve, TRS dangerous vs navigable semicircle.
// ────────────────────────────────────────────────────────────────────

function ReefingBandsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Reefing decision bands</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Wind speed → sail configuration for a representative 35–40 ft
        cruising yacht. The first rule of reefing: when you THINK about
        reefing, reef. It&apos;s faster to shake out than to put in.
      </p>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr
            style={{
              textAlign: "left",
              color: "var(--text-muted)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <th style={{ padding: "6px 8px" }}>Wind</th>
            <th style={{ padding: "6px 8px" }}>Beaufort</th>
            <th style={{ padding: "6px 8px" }}>Configuration</th>
          </tr>
        </thead>
        <tbody>
          {REEFING_BANDS.map((b) => (
            <tr
              key={b.state}
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <td
                style={{
                  padding: "8px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 700,
                }}
              >
                {b.windKn}
              </td>
              <td className="muted" style={{ padding: "8px" }}>
                {b.beaufort}
              </td>
              <td style={{ padding: "8px" }}>
                <div style={{ fontWeight: 600 }}>{b.configuration}</div>
                <div className="small muted" style={{ marginTop: 4 }}>
                  {b.decision}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StormTacticsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Storm tactics</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Six tactics for handling weather beyond what reduced sail can
        cope with. Choice depends on sea room, sea state, crew strength
        + boat design. There&apos;s no universally-right answer — but
        there is a clearly wrong one for each set of conditions.
      </p>
      {STORM_TACTICS.map((t) => (
        <details
          key={t.id}
          className="card"
          style={{ marginBottom: 8 }}
        >
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {t.name}
            <div
              className="small muted"
              style={{ marginTop: 2, fontWeight: 400 }}
            >
              {t.summary}
            </div>
          </summary>
          <p className="small muted" style={{ margin: "8px 0 6px" }}>
            <strong>When:</strong> {t.whenToUse}
          </p>
          <p
            className="small muted"
            style={{ margin: "4px 0 4px", fontWeight: 600 }}
          >
            Setup:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.steps.map((s, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {s}
              </li>
            ))}
          </ol>
          <p
            className="small"
            style={{
              margin: "8px 0 4px",
              padding: 8,
              background: "var(--bg)",
              border: "1px solid var(--border)",
              borderRadius: 4,
            }}
          >
            <strong>Behaviour:</strong> {t.behaviour}
          </p>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function StormSailsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Storm sails — jib + trysail</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Heavy-cloth dedicated storm sails. Both rigged + practised IN
        PORT, not in the conditions you need them.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 10,
        }}
      >
        {STORM_SAILS.map((s) => (
          <div
            key={s.id}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <strong>{s.name}</strong>
            <p className="small" style={{ margin: "4px 0" }}>
              {s.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Area:</strong> {s.area}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Rigging:</strong> {s.rigging}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>When to set:</strong> {s.whenToSet}
            </p>
            <p
              className="small muted"
              style={{ margin: "6px 0 4px", fontWeight: 600 }}
            >
              Pitfalls:
            </p>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {s.pitfalls.map((p, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function StabilityPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>
        Vessel stability — positive, neutral, negative, inverted
      </h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        A monohull yacht is stable in TWO orientations: upright
        (positive stability up to AVS — Angle of Vanishing Stability)
        and inverted (negative stability past AVS). Between them the
        boat capsizes. Sample GZ curve for a 40 ft cruiser (AVS ≈{" "}
        {SAMPLE_AVS_DEG}°):
      </p>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
        <GzCurveFigure size={520} />
      </div>

      {STABILITY_CONCEPTS.map((s) => (
        <div
          key={s.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{s.name}</strong>
          <p
            className="small muted"
            style={{
              margin: "4px 0",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {s.heelRange}
          </p>
          <p className="small" style={{ margin: "4px 0" }}>
            {s.description}
          </p>
          <p
            className="small muted"
            style={{ margin: "4px 0 0", fontStyle: "italic" }}
          >
            <strong>Crew:</strong> {s.crewExperience}
          </p>
        </div>
      ))}

      <p className="small muted" style={{ margin: "10px 0 0" }}>
        Modern cruising yachts have AVS typically 110–130°; ocean
        cruisers + racers 130°+. CE design categories: Cat A (ocean)
        requires AVS ≥ 130°, Cat B (offshore) ≥ 120°.
      </p>
    </div>
  );
}

function TrsPanel() {
  const [hem, setHem] = useState<"N" | "S">("N");
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Tropical Revolving Storms — avoidance</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        TRS = hurricane / typhoon / cyclone — Yachtmaster Ocean
        material. The single key skill: identify which side of the
        track you&apos;re on (dangerous vs navigable semicircle) and
        steer to escape correctly.
      </p>

      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        {(["N", "S"] as const).map((h) => (
          <button
            key={h}
            type="button"
            className={hem === h ? "" : "secondary"}
            onClick={() => setHem(h)}
            style={{ flex: "1 1 140px" }}
            aria-pressed={hem === h}
          >
            {h === "N" ? "Northern hemisphere" : "Southern hemisphere"}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
        <TrsZonesFigure hemisphere={hem} size={360} />
      </div>

      {TRS_ZONES.map((z) => (
        <div
          key={z.id}
          style={{
            border: `1px solid ${z.id === "dangerous" ? "#c0382b" : "#1e6091"}`,
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background:
              z.id === "dangerous"
                ? "rgba(192, 56, 43, 0.05)"
                : "rgba(30, 96, 145, 0.05)",
          }}
        >
          <strong
            style={{
              color: z.id === "dangerous" ? "#c0382b" : "#1e6091",
            }}
          >
            {z.name}
          </strong>
          <p className="small" style={{ margin: "4px 0" }}>
            <strong>Position:</strong> {z.position}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Conditions:</strong> {z.conditions}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Effect:</strong> {z.effect}
          </p>
          <p
            className="small"
            style={{
              margin: "6px 0 0",
              padding: 8,
              background: "rgba(44, 122, 59, 0.06)",
              borderLeft: "3px solid #2c7a3b",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Escape:</strong> {z.escape}
          </p>
        </div>
      ))}

      <h4 style={{ margin: "10px 0 4px", fontSize: 14 }}>
        Summary rules
      </h4>
      <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
        {TRS_RULES.map((r, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            {r}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Berthing + anchoring techniques — surfaced inside AnchoringSection.
// ────────────────────────────────────────────────────────────────────

function BerthingTechniquesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Mooring techniques</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Seven canonical ways to tie a boat up. The right choice depends
        on wind + tide + space + how long you&apos;re staying.
      </p>
      {MOORING_TECHNIQUES.map((t) => (
        <details key={t.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {t.name}
            <div
              className="small muted"
              style={{ marginTop: 2, fontWeight: 400 }}
            >
              {t.summary}
            </div>
          </summary>

          <p className="small muted" style={{ margin: "10px 0 6px" }}>
            <strong>When:</strong> {t.whenToUse}
          </p>

          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Lines required:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.linesRequired.map((l, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {l}
              </li>
            ))}
          </ul>

          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Arrival procedure:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.arrivalSteps.map((s, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {s}
              </li>
            ))}
          </ol>

          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.pitfalls.map((pf, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {pf}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function AnchorTechniquesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Anchoring techniques — beyond a single anchor</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Multi-anchor moors + anchor-watch routines for the times when
        a single bower anchor isn&apos;t the right answer.
      </p>
      {ANCHOR_TECHNIQUES.map((t) => (
        <details key={t.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {t.name}
            <div
              className="small muted"
              style={{ marginTop: 2, fontWeight: 400 }}
            >
              {t.summary}
            </div>
          </summary>

          <p className="small muted" style={{ margin: "10px 0 6px" }}>
            <strong>When:</strong> {t.whenToUse}
          </p>
          <p className="small muted" style={{ margin: "4px 0 6px" }}>
            <strong>Anchors required:</strong> {t.anchorsRequired}
          </p>

          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Procedure:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.steps.map((s, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {s}
              </li>
            ))}
          </ol>

          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.pitfalls.map((pf, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {pf}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Pre-departure + provisioning — combines the consumables calculator,
// the rate-card reference, and the crew safety-brief template.
// ────────────────────────────────────────────────────────────────────

function PredeparturePage() {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Pre-departure + provisioning</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          Two pieces every skipper handles in the days before slipping
          lines: <strong>provisioning</strong> (working out how much
          water, fuel, food + gas to load) and the{" "}
          <strong>crew safety brief</strong> (the structured talk-through
          that prepares new crew for what happens at sea).
        </p>
      </div>

      <ProvisioningCalculator />
      <ConsumableRateCard />
      <CrewBriefPanel />
    </>
  );
}

function ConsumableRateCard() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Consumable rates — per person per day</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Standard planning rates for offshore + coastal sailing. The
        calculator above uses these directly.
      </p>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr
            style={{
              textAlign: "left",
              color: "var(--text-muted)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <th style={{ padding: "6px 8px" }}>Consumable</th>
            <th style={{ padding: "6px 8px" }}>Min</th>
            <th style={{ padding: "6px 8px" }}>Rated</th>
            <th style={{ padding: "6px 8px" }}>Generous</th>
            <th style={{ padding: "6px 8px" }}>Unit</th>
          </tr>
        </thead>
        <tbody>
          {CONSUMABLES.map((c) => (
            <tr
              key={c.id}
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <td style={{ padding: "8px" }}>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div className="small muted" style={{ marginTop: 4 }}>
                  {c.notes}
                </div>
              </td>
              <td
                style={{
                  padding: "8px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {c.minimumPerPersonPerDay}
              </td>
              <td
                style={{
                  padding: "8px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 700,
                }}
              >
                {c.ratedPerPersonPerDay}
              </td>
              <td
                style={{
                  padding: "8px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {c.generousPerPersonPerDay}
              </td>
              <td className="muted" style={{ padding: "8px", fontSize: 12 }}>
                {c.unit}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CrewBriefPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Crew safety brief — template</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Ten sections every skipper covers before slipping lines on a
        new crew. Use this as a printable / scrollable script. Print
        to a single laminated sheet for repeat use.
      </p>
      <ol style={{ paddingLeft: 22, margin: 0 }}>
        {CREW_BRIEF.map((s) => (
          <li key={s.id} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{s.title}</div>
            <p className="small muted" style={{ margin: "4px 0 6px" }}>
              {s.summary}
            </p>
            <p
              className="small muted"
              style={{ margin: "4px 0 4px", fontWeight: 600 }}
            >
              Talking points:
            </p>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {s.talkingPoints.map((t, i) => (
                <li key={i} style={{ marginBottom: 3 }}>
                  {t}
                </li>
              ))}
            </ul>
            <p
              className="small"
              style={{
                margin: "8px 0 0",
                padding: 8,
                background: "rgba(44, 122, 59, 0.06)",
                borderLeft: "3px solid #2c7a3b",
                borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
              }}
            >
              <strong>Demo / show:</strong> {s.demo}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// IALA Region B + IWW panels — surfaced inside BuoyageSection.
// ────────────────────────────────────────────────────────────────────

function RegionGeographyPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>IALA Region A vs Region B</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The world is split into two buoyage regions. Lateral marks are{" "}
        <strong>opposite</strong> between them; everything else
        (cardinals, safe water, isolated danger, special, emergency
        wreck) is identical.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 12,
        }}
      >
        {REGION_GEOGRAPHY.map((r) => (
          <div
            key={r.region}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 12,
              background: "var(--bg-elev)",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
              Region {r.region}
            </div>
            <p
              className="small"
              style={{
                margin: "4px 0",
                fontStyle: "italic",
                color: "var(--accent)",
                fontWeight: 600,
              }}
            >
              {r.mnemonic}
            </p>
            <p className="small" style={{ margin: "6px 0" }}>
              {r.summary}
            </p>
            <p className="small muted" style={{ margin: "6px 0" }}>
              <strong>Port:</strong> {r.portConvention}
            </p>
            <p className="small muted" style={{ margin: "4px 0 6px" }}>
              <strong>Starboard:</strong> {r.starboardConvention}
            </p>
            <p
              className="small muted"
              style={{ margin: "6px 0 2px", fontWeight: 600 }}
            >
              Used in:
            </p>
            <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
              {r.countries.map((c, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p
        className="small muted"
        style={{
          marginTop: 12,
          padding: 10,
          background: "var(--bg-elev)",
          borderLeft: "3px solid var(--accent)",
          borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
        }}
      >
        <strong>Direction of buoyage:</strong> {DIRECTION_OF_BUOYAGE_NOTE}
      </p>
    </div>
  );
}

function RegionDifferencesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>What changes (and what doesn&apos;t)</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Crossing the regional boundary (typically marked on charts)
        only changes the LATERAL mark colour + shape. Everything else
        stays the same.
      </p>
      <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
        {REGION_DIFFERENCES.map((d, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            {d}
          </li>
        ))}
      </ul>
    </div>
  );
}

function IwwMarksPanel() {
  type IwwMark = (typeof IWW_MARKS)[number];
  const byCat: Record<string, IwwMark[]> = {};
  for (const m of IWW_MARKS) {
    (byCat[m.category] ??= []).push(m);
  }
  const order = [
    "prohibited",
    "compulsory",
    "restriction",
    "permissive",
    "indication",
  ];
  const catLabel: Record<string, string> = {
    prohibited: "Prohibitions",
    compulsory: "Compulsory actions",
    restriction: "Restrictions",
    permissive: "Permissions",
    indication: "Information / indications",
  };

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>European Inland Waterways (IWW) marks</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        CEVNI / SIGNI signage used on European inland waterways (Rhine,
        Danube, French + Dutch canals, etc.). Different from marine
        IALA buoyage — important if you cross from sea to canal.
      </p>
      {order.map((cat) => {
        const marks = byCat[cat] ?? [];
        if (marks.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: 12 }}>
            <h4 style={{ margin: "8px 0 4px", fontSize: 14 }}>
              {catLabel[cat]}
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: 8,
              }}
            >
              {marks.map((m) => (
                <div
                  key={m.id}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    padding: 8,
                    background: "var(--bg-elev)",
                  }}
                >
                  <strong>{m.name}</strong>
                  <p className="small muted" style={{ margin: "4px 0" }}>
                    <strong>Appearance:</strong> {m.appearance}
                  </p>
                  <p className="small" style={{ margin: "4px 0" }}>
                    {m.meaning}
                  </p>
                  <p
                    className="small muted"
                    style={{ margin: "4px 0 0", fontStyle: "italic" }}
                  >
                    {m.context}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Powerboat handling at speed — Reference section.
// ────────────────────────────────────────────────────────────────────

function PowerboatHandlingPage() {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Powerboat handling at speed</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          RYA Powerboat Intermediate + Advanced material. Six topics:
          modes of operation (displacement / transition / planing),
          trim, sea-state handling, stopping distance, turning
          modes, and the controlled stop.
        </p>
      </div>
      <HullModesPanel />
      <TrimPanel />
      <SeaHandlingPanel />
      <StoppingDistancePanel />
      <TurningPanel />
    </>
  );
}

function HullModesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Modes of operation</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        A planing-hull powerboat operates in three distinct modes —
        understand which you&apos;re in + how to transition between
        them.
      </p>
      {HULL_MODES.map((m) => (
        <div
          key={m.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{m.name}</strong>
          <p
            className="small muted"
            style={{
              margin: "4px 0",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {m.speedRangeKnots}
          </p>
          <p className="small" style={{ margin: "4px 0" }}>
            {m.behaviour}
          </p>
          <p className="small muted" style={{ margin: "4px 0 0", fontStyle: "italic" }}>
            {m.notes}
          </p>
        </div>
      ))}
    </div>
  );
}

function TrimPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Trim — bow up vs bow down</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Trim tabs and / or outboard tilt control the boat&apos;s
        running attitude. Three settings + when to use each.
      </p>
      {TRIM_SETTINGS.map((t) => (
        <div
          key={t.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{t.name}</strong>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Applied by:</strong> {t.appliedBy}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Attitude:</strong> {t.attitude}
          </p>
          <p className="small" style={{ margin: "4px 0" }}>
            <strong>When:</strong> {t.whenToUse}
          </p>
          <p className="small muted" style={{ margin: "4px 0 4px", fontWeight: 600 }}>
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function SeaHandlingPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Sea-state handling</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Five sea conditions with handling, recommended setup, and
        common mistakes. Following seas + beam-on chop are the two
        that hurt fast powerboats.
      </p>
      {SEA_HANDLING.map((s) => (
        <details key={s.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {s.name}
          </summary>
          <p className="small" style={{ margin: "8px 0" }}>
            {s.description}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Handling:</strong> {s.handling}
          </p>
          <p
            className="small"
            style={{
              margin: "6px 0",
              padding: 8,
              background: "rgba(44, 122, 59, 0.06)",
              borderLeft: "3px solid #2c7a3b",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Recommended:</strong> {s.recommended}
          </p>
          <p className="small muted" style={{ margin: "8px 0 4px", fontWeight: 600 }}>
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {s.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function StoppingDistancePanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Stopping distance + the controlled stop</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Approximate stopping distances for a 6 m sportsboat / RIB at
        various speeds — used as a planning baseline for fast-water
        manoeuvres.
      </p>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr
            style={{
              textAlign: "left",
              color: "var(--text-muted)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <th style={{ padding: "6px 8px" }}>Speed</th>
            <th style={{ padding: "6px 8px" }}>Distance</th>
            <th style={{ padding: "6px 8px" }}>Time</th>
            <th style={{ padding: "6px 8px" }}>Note</th>
          </tr>
        </thead>
        <tbody>
          {STOPPING_DISTANCES.map((s) => (
            <tr
              key={s.speedKnots}
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <td
                style={{
                  padding: "8px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 700,
                }}
              >
                {s.speedKnots} kt
              </td>
              <td style={{ padding: "8px" }}>
                {s.distanceBoatLengths} boat lengths
              </td>
              <td style={{ padding: "8px" }}>{s.timeSeconds} s</td>
              <td className="small muted" style={{ padding: "8px" }}>
                {s.note}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4 style={{ margin: "16px 0 4px", fontSize: 14 }}>
        Controlled-stop procedure
      </h4>
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        {CONTROLLED_STOP.map((s, i) => (
          <li key={i} className="small" style={{ marginBottom: 4 }}>
            {s}
          </li>
        ))}
      </ol>
    </div>
  );
}

function TurningPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Turning + cornering</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Three turning modes — pivot at the dock, sweep on plane,
        banked turn at high speed. Each has its place + each can
        capsize a boat if used wrong.
      </p>
      {TURNING_MODES.map((t) => (
        <div
          key={t.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{t.name}</strong>
          <p className="small" style={{ margin: "4px 0" }}>
            {t.description}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>When:</strong> {t.whenToUse}
          </p>
          <p className="small muted" style={{ margin: "4px 0 4px", fontWeight: 600 }}>
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Passage planning framework — Reference section.
// ────────────────────────────────────────────────────────────────────

function PassagePlanningPage() {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Passage planning — RYA framework</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          The eight-stage workflow every RYA Day Skipper + Coastal
          Skipper passage uses. Builds on the 4 P&apos;s mnemonic
          (Plan · People · Plot · Provisions) with concrete stage-by-
          stage detail.
        </p>
      </div>
      <WorkflowPanel />
      <TidalGatesPanel />
      <ContingencyPanel />
    </>
  );
}

function WorkflowPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Eight-stage workflow</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Each stage feeds the next. Inputs you need; outputs you
        produce; questions you should be able to answer when the
        stage closes.
      </p>
      <ol style={{ paddingLeft: 22, margin: 0 }}>
        {WORKFLOW.map((s) => (
          <li key={s.id} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{s.name}</div>
            <p className="small muted" style={{ margin: "4px 0 4px" }}>
              {s.summary}
            </p>
            <p
              className="small muted"
              style={{
                margin: "4px 0",
                fontStyle: "italic",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              Timing: {s.timing}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 8,
                marginTop: 6,
              }}
            >
              <div>
                <div
                  className="small muted"
                  style={{ fontWeight: 600, marginBottom: 4 }}
                >
                  Inputs
                </div>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {s.inputs.map((x, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div
                  className="small muted"
                  style={{ fontWeight: 600, marginBottom: 4 }}
                >
                  Outputs
                </div>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {s.outputs.map((x, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div
                  className="small muted"
                  style={{ fontWeight: 600, marginBottom: 4 }}
                >
                  Answer at the end of this stage
                </div>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {s.questions.map((x, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function TidalGatesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Common tidal gates</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Familiar UK + European choke points where the tide dictates
        timing. Names + windows for reference; back-solve a departure
        time using the Rule of Twelfths calculator + this list.
      </p>
      {COMMON_TIDAL_GATES.map((g) => (
        <div
          key={g.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{g.name}</strong>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Constraint:</strong> {g.constraint}
          </p>
          <p
            className="small"
            style={{
              margin: "4px 0",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontWeight: 700,
              color: "var(--accent)",
            }}
          >
            Window: {g.window}
          </p>
          <p className="small muted" style={{ margin: "4px 0 0" }}>
            {g.rationale}
          </p>
        </div>
      ))}
    </div>
  );
}

function ContingencyPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Contingency triggers + responses</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Pre-decide your responses to common in-passage problems. A
        plan made in shelter is better than a decision made wet + tired
        at sea.
      </p>
      {COMMON_TRIGGERS.map((t) => (
        <div
          key={t.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <p className="small" style={{ margin: "0 0 4px", fontWeight: 700 }}>
            Trigger: {t.trigger}
          </p>
          <p
            className="small"
            style={{
              margin: "6px 0",
              padding: 8,
              background: "rgba(44, 122, 59, 0.06)",
              borderLeft: "3px solid #2c7a3b",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Action:</strong> {t.action}
          </p>
          <p className="small muted" style={{ margin: "4px 0 0" }}>
            <strong>Comms:</strong> {t.comms}
          </p>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Engine deep-dive panels — extend EngineSection with systems +
// troubleshooting tree.
// ────────────────────────────────────────────────────────────────────

function EngineSystemsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>The six engine systems</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        A marine diesel is a collection of six working systems. Each
        does one job; each has a most-common failure mode worth
        knowing.
      </p>
      {ENGINE_SYSTEMS.map((s) => (
        <details key={s.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {s.name}
            <div
              className="small muted"
              style={{ marginTop: 2, fontWeight: 400 }}
            >
              {s.summary}
            </div>
          </summary>
          <p className="small" style={{ margin: "10px 0 6px" }}>
            {s.purpose}
          </p>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Main components:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {s.components.map((c, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {c}
              </li>
            ))}
          </ul>
          <p
            className="small"
            style={{
              margin: "10px 0 0",
              padding: 8,
              background: "rgba(192, 56, 43, 0.06)",
              borderLeft: "3px solid var(--accent)",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Most-common failure:</strong> {s.failureModeMostCommon}
          </p>
        </details>
      ))}
    </div>
  );
}

function TroubleshootingPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Troubleshooting tree — symptom → action</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Eight common engine symptoms, each with quick-to-cheap
        diagnostic steps, ranked likely causes, and an action plan.
      </p>
      {TROUBLESHOOTING_TREE.map((n) => (
        <details key={n.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {n.symptom}
          </summary>
          <p
            className="small muted"
            style={{ margin: "10px 0 4px", fontWeight: 600 }}
          >
            Diagnose (cheap + quick first):
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {n.diagnose.map((d, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {d}
              </li>
            ))}
          </ol>

          <p
            className="small muted"
            style={{ margin: "10px 0 4px", fontWeight: 600 }}
          >
            Likely causes (most → least probable):
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {n.likelyCauses.map((c, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {c}
              </li>
            ))}
          </ol>

          <p
            className="small"
            style={{
              margin: "10px 0 0",
              padding: 8,
              background: "rgba(44, 122, 59, 0.06)",
              borderLeft: "3px solid #2c7a3b",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Action:</strong> {n.action}
          </p>
        </details>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Ropework + sail repair — Reference section.
// ────────────────────────────────────────────────────────────────────

function RopeworkPage() {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Ropework + sail repair</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          Three domains every Coastal Skipper + Yachtmaster student
          should be able to perform: <strong>splices</strong>{" "}
          (permanent joins in 3-strand rope), <strong>whippings</strong>{" "}
          (finishing rope ends to prevent fraying), and{" "}
          <strong>sail repair</strong> (emergency + ashore).
        </p>
      </div>
      <SplicesPanel />
      <WhippingsPanel />
      <SailRepairPanel />
    </>
  );
}

function SplicesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Splices</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Four canonical splices. Eye splice is the workhorse;
        short + long join two ropes; back splice finishes an end.
        All retain 70–95% of the rope&apos;s MBL when made
        correctly.
      </p>
      {SPLICES.map((s) => (
        <details key={s.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {s.name}
            <div
              className="small muted"
              style={{ marginTop: 2, fontWeight: 400 }}
            >
              {s.summary}
            </div>
          </summary>
          <p className="small muted" style={{ margin: "10px 0 6px" }}>
            <strong>When:</strong> {s.whenToUse}
          </p>
          <p className="small muted" style={{ margin: "4px 0 6px" }}>
            <strong>Tucks:</strong> {s.tucks}
          </p>
          <p className="small muted" style={{ margin: "4px 0 6px" }}>
            <strong>Strength retained:</strong> {s.strengthRetention}
          </p>

          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Procedure:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {s.steps.map((st, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {st}
              </li>
            ))}
          </ol>

          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {s.pitfalls.map((pf, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {pf}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function WhippingsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Whippings</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Four ways to bind a rope end so the strands don&apos;t unlay.
        Common is the quick-and-dirty; sailmaker&apos;s is the gold
        standard.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 10,
        }}
      >
        {WHIPPINGS.map((w) => (
          <div
            key={w.id}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <strong>{w.name}</strong>
            <p className="small" style={{ margin: "4px 0" }}>
              {w.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>When:</strong> {w.whenToUse}
            </p>
            <p
              className="small muted"
              style={{ margin: "4px 0 4px", fontWeight: 600 }}
            >
              Procedure:
            </p>
            <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {w.steps.map((st, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {st}
                </li>
              ))}
            </ol>
            <p
              className="small muted"
              style={{ margin: "6px 0 2px", fontWeight: 600 }}
            >
              Pitfalls:
            </p>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {w.pitfalls.map((pf, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {pf}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function SailRepairPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Sail repair — emergency + ashore</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Five common sail-damage scenarios. Emergency repair at sea
        with the sail-repair kit; permanent repair by a sailmaker
        ashore.
      </p>
      {SAIL_REPAIRS.map((r) => (
        <div
          key={r.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{r.name}</strong>
          <p className="small" style={{ margin: "4px 0" }}>
            {r.summary}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Damage:</strong> {r.damage}
          </p>
          <p
            className="small"
            style={{
              margin: "6px 0",
              padding: 8,
              background: "rgba(192, 56, 43, 0.06)",
              borderLeft: "3px solid var(--accent)",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>At sea:</strong> {r.emergencyRepair}
          </p>
          <p
            className="small"
            style={{
              margin: "4px 0",
              padding: 8,
              background: "rgba(44, 122, 59, 0.06)",
              borderLeft: "3px solid #2c7a3b",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Ashore:</strong> {r.ashoreRepair}
          </p>
          <p
            className="small muted"
            style={{ margin: "6px 0 2px", fontWeight: 600 }}
          >
            Materials:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {r.materials.map((m, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {m}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tender handling — Reference section.
// ────────────────────────────────────────────────────────────────────

function TenderPage() {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Tender handling</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
          RYA Tender course + Powerboat + Day Skipper material.
          Five topics: tender types, painters, towing techniques,
          boarding, and davit operation.
        </p>
      </div>
      <TenderTypesPanel />
      <PaintersPanel />
      <TowingPanel />
      <BoardingPanel />
      <DavitsPanel />
    </>
  );
}

function TenderTypesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Tender types</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Four common tender configurations. Choice depends on yacht
        size, stowage, and how you use the tender.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 10,
        }}
      >
        {TENDERS.map((t) => (
          <div
            key={t.id}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              background: "var(--bg-elev)",
            }}
          >
            <strong>{t.name}</strong>
            <p className="small" style={{ margin: "4px 0" }}>
              {t.summary}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Best for:</strong> {t.bestFor}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              {t.proCons}
            </p>
            <p
              className="small muted"
              style={{
                margin: "4px 0 0",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              {t.capacity}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaintersPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Painters — single, double, bridled</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Three painter setups, each for different conditions + risk
        tolerance.
      </p>
      {PAINTERS.map((p) => (
        <div
          key={p.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{p.name}</strong>
          <p className="small" style={{ margin: "4px 0" }}>
            {p.summary}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>When:</strong> {p.whenToUse}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Knots:</strong> {p.knots}
          </p>
          <p
            className="small muted"
            style={{ margin: "4px 0 4px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {p.pitfalls.map((pf, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {pf}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TowingPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Towing techniques</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Five towing scenarios, each with its own painter length, speed
        + pitfalls.
      </p>
      {TOWING_TECHNIQUES.map((t) => (
        <details key={t.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {t.name}
          </summary>
          <p className="small" style={{ margin: "10px 0 6px" }}>
            {t.summary}
          </p>
          <p
            className="small muted"
            style={{
              margin: "4px 0",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            <strong>Speed:</strong> {t.speedKnots}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Painter length:</strong> {t.painterLength}
          </p>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function BoardingPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Boarding + disembarking — rules</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Nine rules every tender operator should follow. Boarding
        accidents are the most common tender injury at sea.
      </p>
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        {BOARDING_RULES.map((r, i) => (
          <li key={i} className="small" style={{ marginBottom: 4 }}>
            {r}
          </li>
        ))}
      </ol>
    </div>
  );
}

function DavitsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Davits + stowage</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Three ways to lift the tender clear of the water — for
        overnight stowage on passage or when leaving the boat.
      </p>
      {DAVITS.map((d) => (
        <div
          key={d.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{d.name}</strong>
          <p className="small" style={{ margin: "4px 0" }}>
            {d.summary}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>When:</strong> {d.whenToUse}
          </p>
          <p
            className="small muted"
            style={{ margin: "4px 0 4px", fontWeight: 600 }}
          >
            Procedure:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {d.procedure.map((step, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {step}
              </li>
            ))}
          </ol>
          <p
            className="small muted"
            style={{ margin: "6px 0 2px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {d.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Outboard panel — extends EngineSection.
// ────────────────────────────────────────────────────────────────────

function OutboardPanel() {
  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Outboard motors</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
          Outboards are a different beast from inboard diesels —
          petrol (mostly), 2-stroke or 4-stroke, simpler to service.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 10,
          }}
        >
          {OUTBOARDS.map((o) => (
            <div
              key={o.id}
              style={{
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: 10,
                background: "var(--bg-elev)",
              }}
            >
              <strong>{o.name}</strong>
              <p className="small" style={{ margin: "4px 0" }}>
                {o.summary}
              </p>
              <p className="small muted" style={{ margin: "4px 0" }}>
                <strong>Characteristics:</strong> {o.characteristics}
              </p>
              <p className="small muted" style={{ margin: "4px 0" }}>
                <strong>Servicing:</strong> {o.servicing}
              </p>
              <p
                className="small muted"
                style={{ margin: "4px 0 0", fontStyle: "italic" }}
              >
                {o.proCons}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Outboard maintenance procedures</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
          Six procedures covering routine + annual maintenance.
        </p>
        {OUTBOARD_PROCEDURES.map((p) => (
          <details key={p.id} className="card" style={{ marginBottom: 8 }}>
            <summary
              style={{
                cursor: "pointer",
                listStyle: "none",
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              {p.name}
              <div
                className="small muted"
                style={{ marginTop: 2, fontWeight: 400 }}
              >
                {p.appliesTo.join(", ")} · {p.duration}
              </div>
            </summary>
            <p className="small muted" style={{ margin: "10px 0 6px" }}>
              <strong>When:</strong> {p.whenToDo}
            </p>
            <p
              className="small muted"
              style={{ margin: "8px 0 4px", fontWeight: 600 }}
            >
              Tools:
            </p>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {p.tools.map((t, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {t}
                </li>
              ))}
            </ul>
            <p
              className="small muted"
              style={{ margin: "8px 0 4px", fontWeight: 600 }}
            >
              Procedure:
            </p>
            <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {p.steps.map((s, i) => (
                <li key={i} style={{ marginBottom: 3 }}>
                  {s}
                </li>
              ))}
            </ol>
            <p
              className="small muted"
              style={{ margin: "8px 0 4px", fontWeight: 600 }}
            >
              Pitfalls:
            </p>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {p.pitfalls.map((pf, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  {pf}
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Kill cord — the most important rule</h3>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
          The kill cord is the single most important safety device on
          a fast powerboat. Multiple fatalities have occurred when it
          was bypassed or removed.
        </p>
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {KILL_CORD_RULES.map((r, i) => (
            <li key={i} className="small" style={{ marginBottom: 4 }}>
              {r}
            </li>
          ))}
        </ol>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Charts panels — extends ChartworkSection.
// ────────────────────────────────────────────────────────────────────

function ProjectionsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Map projections</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Four projections used in marine navigation. Mercator is the
        workhorse; gnomonic is the specialty tool for great-circle
        routes.
      </p>
      {PROJECTIONS.map((p) => (
        <details key={p.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {p.name}
            <div
              className="small muted"
              style={{ marginTop: 2, fontWeight: 400 }}
            >
              {p.summary}
            </div>
          </summary>
          <p className="small muted" style={{ margin: "10px 0 4px" }}>
            <strong>Use:</strong> {p.use}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Properties:</strong> {p.properties}
          </p>
          <p
            className="small"
            style={{
              margin: "6px 0",
              padding: 8,
              background: "rgba(44, 122, 59, 0.06)",
              borderLeft: "3px solid #2c7a3b",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Navigation impact:</strong> {p.navigationImpact}
          </p>
        </details>
      ))}
    </div>
  );
}

function DatumsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Datums — WGS84 vs the rest</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        GPS speaks WGS84. Modern charts use WGS84 or compatible
        datums (ETRS89, NAD83). Old charts may use OSGB36, ED50 or
        others — and the offset can exceed 100 metres.
      </p>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr
            style={{
              textAlign: "left",
              color: "var(--text-muted)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <th style={{ padding: "6px 8px" }}>Datum</th>
            <th style={{ padding: "6px 8px" }}>Region</th>
            <th style={{ padding: "6px 8px" }}>Offset from WGS84</th>
            <th style={{ padding: "6px 8px" }}>Navigation impact</th>
          </tr>
        </thead>
        <tbody>
          {CHART_DATUMS.map((d) => (
            <tr
              key={d.id}
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <td
                style={{
                  padding: "8px",
                  fontWeight: 700,
                }}
              >
                {d.name}
                <div className="small muted" style={{ marginTop: 4, fontWeight: 400 }}>
                  {d.summary}
                </div>
              </td>
              <td style={{ padding: "8px", verticalAlign: "top" }} className="small">
                {d.region}
              </td>
              <td
                style={{
                  padding: "8px",
                  verticalAlign: "top",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12,
                }}
              >
                {d.offsetFromWgs84}
              </td>
              <td className="small muted" style={{ padding: "8px", verticalAlign: "top" }}>
                {d.navigationImpact}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartSymbolsPanel() {
  type CSym = (typeof CHART_SYMBOLS)[number];
  const byCat: Record<string, CSym[]> = {};
  for (const s of CHART_SYMBOLS) {
    (byCat[s.category] ??= []).push(s);
  }
  const order = [
    "natural-features",
    "cultural-features",
    "depths-and-soundings",
    "rocks-and-wrecks",
    "lights",
    "buoyage",
    "services",
    "miscellaneous",
  ];
  const catLabel: Record<string, string> = {
    "natural-features": "Natural features",
    "cultural-features": "Cultural features",
    "depths-and-soundings": "Depths + soundings",
    "rocks-and-wrecks": "Rocks + wrecks",
    lights: "Lights",
    buoyage: "Buoyage marks",
    services: "Services",
    miscellaneous: "Miscellaneous",
  };

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Chart symbols — Admiralty 5011 reference</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        A representative selection of the symbols used on UK + European
        Admiralty marine charts. Full reference is published as INT 1
        / Admiralty Catalogue 5011 — a chart-symbol dictionary every
        skipper should have access to.
      </p>
      {order.map((cat) => {
        const syms = byCat[cat] ?? [];
        if (syms.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: 14 }}>
            <h4 style={{ margin: "8px 0 4px", fontSize: 14 }}>
              {catLabel[cat]}
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 8,
              }}
            >
              {syms.map((s) => (
                <div
                  key={s.id}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    padding: 8,
                    background: "var(--bg-elev)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "baseline",
                      marginBottom: 4,
                    }}
                  >
                    <strong>{s.name}</strong>
                    <span
                      className="small muted"
                      style={{
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 11,
                      }}
                    >
                      5011 § {s.ref}
                    </span>
                  </div>
                  <p className="small" style={{ margin: "4px 0" }}>
                    {s.description}
                  </p>
                  <p
                    className="small muted"
                    style={{ margin: "4px 0 0", fontStyle: "italic" }}
                  >
                    {s.meaning}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Priority-closeout panels — advanced sailing, pilotage fixes,
// tides streams, wind from isobars, shipping forecast, radio
// licensing + RT log.
// ────────────────────────────────────────────────────────────────────

function AdvancedSailingPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Advanced dinghy techniques (NSS L3-4)</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Racing-grade skills + emergency recovery techniques. Practise
        in benign conditions before relying on them.
      </p>
      {ADVANCED_TECHNIQUES.map((t) => (
        <details key={t.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {t.name}
            <div className="small muted" style={{ marginTop: 2, fontWeight: 400 }}>
              {t.course} · {t.summary}
            </div>
          </summary>
          <p className="small muted" style={{ margin: "10px 0 6px" }}>
            <strong>When:</strong> {t.whenToUse}
          </p>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Procedure:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.steps.map((s, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {s}
              </li>
            ))}
          </ol>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {t.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function LaunchProceduresPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Launching + recovery</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Three venues — slipway, beach, pontoon. Each has its own
        sequence + pitfalls.
      </p>
      {LAUNCH_PROCEDURES.map((l) => (
        <details key={l.venue} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {l.name}
            <div className="small muted" style={{ marginTop: 2, fontWeight: 400 }}>
              {l.summary}
            </div>
          </summary>
          <p className="small muted" style={{ margin: "10px 0 6px" }}>
            <strong>Conditions:</strong> {l.conditions}
          </p>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Launch:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {l.launchSteps.map((s, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {s}
              </li>
            ))}
          </ol>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Recovery:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {l.recoverySteps.map((s, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {s}
              </li>
            ))}
          </ol>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {l.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function AdvancedFixesPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Advanced fixes — three techniques</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Beyond the basic 3-point fix. Two bearings + a transit is the
        most accurate; running fix is the offshore standby; doubling
        the angle is a quick distance-off estimator.
      </p>
      {ADVANCED_FIXES.map((f) => (
        <details key={f.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {f.name}
            <div className="small muted" style={{ marginTop: 2, fontWeight: 400 }}>
              {f.summary}
            </div>
          </summary>
          <p className="small muted" style={{ margin: "10px 0 6px" }}>
            <strong>When:</strong> {f.whenToUse}
          </p>
          <p
            className="small muted"
            style={{ margin: "8px 0 4px", fontWeight: 600 }}
          >
            Procedure:
          </p>
          <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {f.steps.map((s, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {s}
              </li>
            ))}
          </ol>
          <p
            className="small"
            style={{
              margin: "8px 0",
              padding: 8,
              background: "rgba(44, 122, 59, 0.06)",
              borderLeft: "3px solid #2c7a3b",
              borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
            }}
          >
            <strong>Accuracy:</strong> {f.accuracy}
          </p>
          <p
            className="small muted"
            style={{ margin: "4px 0", fontWeight: 600 }}
          >
            Pitfalls:
          </p>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {f.pitfalls.map((p, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                {p}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function TidalStreamsPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Tidal streams — sources + lookups</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Four ways to look up tidal stream direction + speed. The atlas
        is for the broad picture; the chart diamond is for the
        accurate fix; the plotter is the modern shortcut; the almanac
        is the printed alternative.
      </p>
      {TIDAL_STREAM_SOURCES.map((s) => (
        <div
          key={s.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{s.name}</strong>
          <p className="small" style={{ margin: "4px 0" }}>
            {s.summary}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>How to use:</strong> {s.howToUse}
          </p>
          <p className="small muted" style={{ margin: "4px 0 0", fontStyle: "italic" }}>
            <strong>Applicability:</strong> {s.applicability}
          </p>
        </div>
      ))}
    </div>
  );
}

function XtePanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Cross-track error (XTE)</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The perpendicular distance from your current position to your
        intended track. Reported by GPS + driven by tidal stream +
        leeway + helmsman error.
      </p>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {XTE_NOTES.map((n, i) => (
          <li key={i} className="small" style={{ marginBottom: 4 }}>
            {n}
          </li>
        ))}
      </ul>
      <p
        className="small"
        style={{
          margin: "12px 0 0",
          padding: 10,
          background: "var(--bg-elev)",
          borderLeft: "3px solid var(--accent)",
          borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
        }}
      >
        <strong>ETA under tide</strong> — a quick formula:{" "}
        <code>SOG = SOW + net favourable stream</code>; then{" "}
        <code>ETA = distance / SOG</code>. Use the average stream over
        the passage when applying — and add 30% for contingency on
        passage planning.
      </p>
    </div>
  );
}

function IsobarWindPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Wind from isobars — geostrophic, gradient + spacing</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Reading a synoptic chart: the isobar pattern tells you wind
        direction (parallel to isobars) + strength (closer-spaced =
        stronger wind).
      </p>
      {ISOBAR_WIND_RULES.map((r) => (
        <div
          key={r.id}
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 10,
            marginBottom: 8,
            background: "var(--bg-elev)",
          }}
        >
          <strong>{r.name}</strong>
          <p className="small" style={{ margin: "4px 0" }}>
            {r.summary}
          </p>
          <p className="small muted" style={{ margin: "4px 0 0" }}>
            {r.detail}
          </p>
        </div>
      ))}
    </div>
  );
}

function ShippingForecastPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Shipping Forecast — reading + transcription</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The UK Shipping Forecast is broadcast four times a day in a
        rigid structure. Knowing the structure makes it easier to
        catch + transcribe what you need.
      </p>
      <ol style={{ paddingLeft: 22, margin: 0 }}>
        {SHIPPING_FORECAST_STRUCTURE.map((s) => (
          <li key={s.id} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700 }}>{s.name}</div>
            <p className="small muted" style={{ margin: "2px 0" }}>
              <strong>Contains:</strong> {s.contains}
            </p>
            <p className="small muted" style={{ margin: "2px 0" }}>
              <strong>Listen for:</strong> {s.listenFor}
            </p>
          </li>
        ))}
      </ol>
      <h4 style={{ margin: "12px 0 4px", fontSize: 14 }}>Transcription tips</h4>
      <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
        {TRANSCRIPTION_TIPS.map((t, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RadioLicencingPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Radio licensing — what + who + how</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Two licences for UK leisure sailors: the SHIP RADIO LICENCE
        (boat-specific, free) and the SRC OPERATOR&apos;S AUTHORITY
        (person-specific, requires the SRC certificate). LRC + GMDSS
        GRC are for ocean / commercial use respectively.
      </p>
      {RADIO_LICENCES.map((l) => (
        <details key={l.id} className="card" style={{ marginBottom: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {l.name}
            <div className="small muted" style={{ marginTop: 2, fontWeight: 400 }}>
              {l.summary}
            </div>
          </summary>
          <p className="small muted" style={{ margin: "8px 0" }}>
            <strong>Covers:</strong> {l.covers}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Issuer:</strong> {l.issuer}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Validity:</strong> {l.validity}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Cost:</strong> {l.cost}
          </p>
          <p className="small muted" style={{ margin: "4px 0" }}>
            <strong>Application:</strong> {l.application}
          </p>
        </details>
      ))}
    </div>
  );
}

function RtLogPanel() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>RT log — keeping a radio log</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        The RT log records every radio communication. Required by
        Ofcom + admissible in court for incident investigations.
      </p>
      <h4 style={{ margin: "8px 0 4px", fontSize: 14 }}>Columns</h4>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse", marginBottom: 12 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
            <th style={{ padding: "6px 8px" }}>Column</th>
            <th style={{ padding: "6px 8px" }}>What goes here</th>
            <th style={{ padding: "6px 8px" }}>Example</th>
          </tr>
        </thead>
        <tbody>
          {RT_LOG_COLUMNS.map((c) => (
            <tr key={c.name} style={{ borderBottom: "1px solid var(--border)" }}>
              <td style={{ padding: "8px", fontWeight: 700 }}>{c.name}</td>
              <td style={{ padding: "8px" }} className="small">{c.contents}</td>
              <td
                style={{
                  padding: "8px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 11,
                }}
                className="muted"
              >
                {c.example}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4 style={{ margin: "8px 0 4px", fontSize: 14 }}>Retention + usage</h4>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {RT_LOG_RETENTION.map((r, i) => (
          <li key={i} className="small" style={{ marginBottom: 4 }}>
            {r}
          </li>
        ))}
      </ul>
    </div>
  );
}
